import * as React from "react";
import { browserHistory } from "react-router";
import { updateEstimateWithToken, fetchWebOrderFromToken, estimatePdfFromTokenUrl, WebOrderFromToken } from "../../tgccRequest";
import * as Tgcc from "../../tgccGlobal";
import { ErrorMessages } from "../../tgccGlobal";
import { EstimateErrorPopup, EstimateConfirmationPopup } from "../ErrorPopupModal";
import { getSharedEstimateErrorHeading } from "./estimateErrorHeading";
import {
    ApiError,
    CardInput,
    ChargeResult,
    SavedCard,
    SavedBankAccount,
    chargeOneTimeBank,
    chargeSavedBank,
    chargeOneTimeToken,
    chargeSavedCard,
    fetchSavedPaymentMethods,
    getFailedChargeUserMessage,
    saveBankOnFile,
    saveCardOnFile,
    tokenizeBankInBrowser,
    tokenizeCardInBrowser,
} from "./qbPaymentsClient";
import { BackendCardFundingType, resolveCardFundingTypeFromCardNumber } from "./handyBinLookup";

import * as FaIcons from "react-icons/fa";

/** Must match server `paymentTerms` exactly to show the card/ACH checkout block. */
const PREPAID_CREDIT_CARD_PAYMENT_TERMS = "Approved Prepaid";

/** Matches TGCC card processing fee line (not applied to debit). */
const CARD_PROCESSING_FEE_RATE = 0.03;

type BinPreviewPhase = "short" | "loading" | "success" | "unknown";

function normalizeStoredCardFunding(raw?: string | null): BackendCardFundingType | "unknown" {
    if (raw == null || String(raw).trim() === "") return "unknown";
    const n = String(raw).trim().replace(/[\s_]/g, "").toLowerCase();
    if (n === "debit") return "Debit";
    if (n === "credit") return "Credit";
    if (n === "prepaid") return "Prepaid";
    return "unknown";
}

function fundingLabel(f: BackendCardFundingType | "unknown"): string {
    if (f === "unknown") return "Not verified";
    if (f === "Debit") return "Debit";
    if (f === "Credit") return "Credit";
    return "Prepaid";
}

function fundingBadgeStyle(f: BackendCardFundingType | "unknown"): React.CSSProperties {
    const base: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3rem",
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        padding: "0.25rem 0.6rem",
        borderRadius: "999px",
        border: "1px solid transparent",
        whiteSpace: "nowrap",
    };
    if (f === "Debit") return { ...base, background: "#d1fae5", color: "#047857", borderColor: "#a7f3d0" };
    if (f === "Credit") return { ...base, background: "#e0e7ff", color: "#3730a3", borderColor: "#c7d2fe" };
    if (f === "Prepaid") return { ...base, background: "#fef3c7", color: "#b45309", borderColor: "#fde68a" };
    return { ...base, background: "#f1f5f9", color: "#475569", borderColor: "#e2e8f0" };
}

/** Legacy unscoped keys — cleared on load so one browser profile is not stuck with another estimate's cached payment metadata. */
const LEGACY_ESTIMATE_APPROVE_SAVED_CARDS_KEY = "EstimateApproveSavedCards";
const LEGACY_ESTIMATE_APPROVE_SAVED_BANK_KEY = "EstimateApproveSavedBankAccounts";

/** Backend may send `isDefault`; keep explicit so TS is satisfied even if the client type lags. */
type SavedBankAccountWithOptionalDefault = SavedBankAccount & { isDefault?: boolean };

interface EstimateApproveProps {
    location: {
        query: {
            token?: string;
        };
    };
}

interface EstimateApproveState {
    webOrderNumber: string | null;
    isLoading: boolean;
    errorMessage: string | null;
    isSubmitting: boolean;
    showConfirmation?: boolean;
    customerId: string;
    amount: string;
    currency: string;
    description: string;
    paymentMode: "new" | "saved";
    paymentRail: "card" | "ach";
    saveCardForFuture: boolean;
    achMode: "new" | "saved";
    saveBankForFuture: boolean;
    selectedSavedCardId: string;
    selectedSavedBankAccountId: string;
    savedCards: SavedCard[];
    savedBankAccounts: SavedBankAccount[];
    cardInput: CardInput;
    processingStep: string | null;
    paymentError: string | null;
    chargeResult: ChargeResult | null;
    paymentId: string | null;
    cardId: string | null;
    isPaymentProcessing: boolean;
    achInput: {
        accountHolderName: string;
        routingNumber: string;
        accountNumber: string;
        accountType: "checking" | "savings";
    };
    paymentTerms: string | null;
    binPreviewPhase: BinPreviewPhase;
    binPreviewFunding?: BackendCardFundingType;
    shippingAddress?: string;
    shippingAddressConfirmed: boolean;
    estimatePdfAvailable: boolean;
}

class EstimateApprove extends React.Component<EstimateApproveProps, EstimateApproveState> {
    private _binDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private _binLookupSeq = 0;
    private paymentErrorBannerRef = React.createRef<HTMLDivElement>();

    getRawDataBlockedMessage = (): string =>
        "Payment tokenization provider is not available. Card and bank details must be tokenized directly with Intuit before submitting.";

    getTokenPreview = (token?: string | null): string | null => {
        if (!token) return null;
        const trimmed = token.trim();
        if (!trimmed) return null;
        return trimmed.length <= 8 ? trimmed : `${trimmed.slice(0, 8)}...`;
    };

    logCardSaveFlow = (message: string, details?: Record<string, unknown>) => {
        console.info("[EstimateApprove][saved-card]", message, details || {});
    };

    getWebOrderTokenValue = (): string => {
        return this.props.location?.query?.token || "";
    };

    refreshSavedCardsFromBackend = async () => {
        const webOrderToken = this.getWebOrderTokenValue();
        if (!webOrderToken) return;
        if (this.state.paymentTerms !== PREPAID_CREDIT_CARD_PAYMENT_TERMS) return;
        if (this.state.isPaymentProcessing) return;
        try {
            const savedMethodsRes = await fetchSavedPaymentMethods(webOrderToken);
            const qbCustomerId = savedMethodsRes.qbCustomerId;
            const cards = savedMethodsRes.cards;
            const accounts = savedMethodsRes.accounts;
            this.saveSavedCards(cards, webOrderToken);
            this.saveSavedBankAccounts(accounts, webOrderToken);
            this.setState((prev) => {
                const base = {
                    customerId: qbCustomerId || prev.customerId,
                    savedCards: cards,
                    savedBankAccounts: accounts,
                };
                if (prev.isPaymentProcessing) {
                    return base;
                }
                return { ...base, ...this.getDefaultPaymentPreferences(cards, accounts) };
            });
        } catch (err) {
            console.error("Failed to fetch saved cards:", err);
            const fallbackCards = this.loadSavedCards(webOrderToken);
            const fallbackAccounts = this.loadSavedBankAccounts(webOrderToken);
            this.setState((prev) => {
                const base = {
                    savedCards: fallbackCards,
                    savedBankAccounts: fallbackAccounts,
                };
                if (prev.isPaymentProcessing) {
                    return base;
                }
                return { ...base, ...this.getDefaultPaymentPreferences(fallbackCards, fallbackAccounts) };
            });
        }
    };

    getWebOrderAuth = (): { webOrderToken?: string; webOrderNumber?: string } | undefined => {
        const webOrderToken = this.props.location?.query?.token;
        if (!webOrderToken) return undefined;
        const webOrderNumber = this.state.webOrderNumber;
        if (!webOrderNumber) return undefined;
        return { webOrderToken, webOrderNumber };
    };

    pickPreferredSavedCardId = (cards: SavedCard[]): string => {
        const preferred = cards.find((c) => c.isDefault);
        return (preferred || cards[0])?.cardId || "";
    };

    pickPreferredSavedBankAccountId = (accounts: SavedBankAccountWithOptionalDefault[]): string => {
        const preferred = accounts.find((a) => a.isDefault);
        return (preferred || accounts[0])?.bankAccountId || "";
    };

    getDefaultPaymentPreferences = (
        cards: SavedCard[],
        accounts: SavedBankAccountWithOptionalDefault[]
    ): Pick<
        EstimateApproveState,
        "paymentRail" | "paymentMode" | "achMode" | "selectedSavedCardId" | "selectedSavedBankAccountId"
    > => {
        const hasCards = cards.length > 0;
        const hasBank = accounts.length > 0;
        const cardId = this.pickPreferredSavedCardId(cards);
        const bankId = this.pickPreferredSavedBankAccountId(accounts);

        if (hasCards && hasBank) {
            return {
                paymentRail: "card",
                paymentMode: "saved",
                achMode: "saved",
                selectedSavedCardId: cardId,
                selectedSavedBankAccountId: bankId,
            };
        }
        if (hasCards) {
            return {
                paymentRail: "card",
                paymentMode: "saved",
                achMode: "new",
                selectedSavedCardId: cardId,
                selectedSavedBankAccountId: "",
            };
        }
        if (hasBank) {
            return {
                paymentRail: "ach",
                paymentMode: "new",
                achMode: "saved",
                selectedSavedCardId: "",
                selectedSavedBankAccountId: bankId,
            };
        }
        return {
            paymentRail: "card",
            paymentMode: "new",
            achMode: "new",
            selectedSavedCardId: "",
            selectedSavedBankAccountId: "",
        };
    };

    constructor(props: EstimateApproveProps) {
        super(props);
        const urlToken = (props.location?.query?.token || "").trim();
        const initialSavedCards = this.loadSavedCards(urlToken);
        const initialSavedBankAccounts = this.loadSavedBankAccounts(urlToken);
        this.state = {
            webOrderNumber: null,
            isLoading: true,
            errorMessage: null,
            isSubmitting: false,
            showConfirmation: false,
            customerId: this.getDefaultCustomerId(),
            amount: "",
            currency: "USD",
            description: "Deposit for estimate",
            saveCardForFuture: true,
            saveBankForFuture: true,
            savedCards: initialSavedCards,
            savedBankAccounts: initialSavedBankAccounts,
            ...this.getDefaultPaymentPreferences(initialSavedCards, initialSavedBankAccounts),
            cardInput: {
                number: "",
                expMonth: 0,
                expYear: 0,
                cvc: "",
                name: "",
                postalCode: "",
            },
            processingStep: null,
            paymentError: null,
            chargeResult: null,
            paymentId: null,
            cardId: null,
            isPaymentProcessing: false,
            achInput: {
                accountHolderName: "",
                routingNumber: "",
                accountNumber: "",
                accountType: "checking",
            },
            paymentTerms: null,
            binPreviewPhase: "short",
            binPreviewFunding: undefined,
            shippingAddress: undefined,
            shippingAddressConfirmed: false,
            estimatePdfAvailable: false,
        };
    }

    componentDidUpdate(_prevProps: EstimateApproveProps, prevState: EstimateApproveState): void {
        if (this.state.paymentError && this.state.paymentError !== prevState.paymentError) {
            const el = this.paymentErrorBannerRef.current;
            if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }

    componentWillUnmount(): void {
        if (this._binDebounceTimer) {
            clearTimeout(this._binDebounceTimer);
            this._binDebounceTimer = null;
        }
        this._binLookupSeq++;
    }

    clearBinFundingPreview = (): void => {
        if (this._binDebounceTimer) {
            clearTimeout(this._binDebounceTimer);
            this._binDebounceTimer = null;
        }
        this._binLookupSeq++;
        this.setState({ binPreviewPhase: "short", binPreviewFunding: undefined });
    };

    updateBinPreviewFromNumber = (cardNumber: string): void => {
        if (this.state.paymentRail !== "card" || this.state.paymentMode !== "new") return;
        const digits = String(cardNumber || "").replace(/\D/g, "");
        if (digits.length < 8) {
            if (this._binDebounceTimer) {
                clearTimeout(this._binDebounceTimer);
                this._binDebounceTimer = null;
            }
            this.setState({ binPreviewPhase: "short", binPreviewFunding: undefined });
            return;
        }
        this.setState({ binPreviewPhase: "loading" });
        if (this._binDebounceTimer) clearTimeout(this._binDebounceTimer);
        this._binDebounceTimer = setTimeout(async () => {
            this._binDebounceTimer = null;
            const seq = ++this._binLookupSeq;
            const funding = await resolveCardFundingTypeFromCardNumber(cardNumber);
            if (seq !== this._binLookupSeq) return;
            if (this.state.paymentRail !== "card" || this.state.paymentMode !== "new") return;
            if (funding) {
                this.setState({ binPreviewPhase: "success", binPreviewFunding: funding });
            } else {
                this.setState({ binPreviewPhase: "unknown", binPreviewFunding: undefined });
            }
        }, 420);
    };

    getEffectiveFundingForFee = (): BackendCardFundingType | "unknown" => {
        if (this.state.paymentRail !== "card") return "unknown";
        if (this.state.paymentMode === "saved") {
            const c = this.state.savedCards.find((x) => x.cardId === this.state.selectedSavedCardId);
            return normalizeStoredCardFunding(c?.cardFundingType);
        }
        if (this.state.binPreviewPhase === "success" && this.state.binPreviewFunding) {
            return this.state.binPreviewFunding;
        }
        return "unknown";
    };

    getDefaultCustomerId = (): string => {
        try {
            const raw = localStorage.getItem("LoginData");
            if (!raw) return "1";
            const parsed = JSON.parse(raw);
            return parsed?.id ? String(parsed.id) : "1";
        } catch (err) {
            return "1";
        }
    };

    savedCardsStorageKey = (webOrderToken: string): string | null => {
        const t = webOrderToken.trim();
        if (!t) return null;
        return `${LEGACY_ESTIMATE_APPROVE_SAVED_CARDS_KEY}:${t}`;
    };

    savedBankAccountsStorageKey = (webOrderToken: string): string | null => {
        const t = webOrderToken.trim();
        if (!t) return null;
        return `${LEGACY_ESTIMATE_APPROVE_SAVED_BANK_KEY}:${t}`;
    };

    loadSavedCards = (webOrderToken: string): SavedCard[] => {
        const key = this.savedCardsStorageKey(webOrderToken);
        if (!key) return [];
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            const cards = JSON.parse(raw);
            return Array.isArray(cards) ? cards : [];
        } catch {
            return [];
        }
    };

    saveSavedCards = (cards: SavedCard[], webOrderToken: string) => {
        const key = this.savedCardsStorageKey(webOrderToken);
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(cards));
        } catch {
            /* ignore quota / private mode */
        }
    };

    loadSavedBankAccounts = (webOrderToken: string): SavedBankAccount[] => {
        const key = this.savedBankAccountsStorageKey(webOrderToken);
        if (!key) return [];
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            const accounts = JSON.parse(raw);
            return Array.isArray(accounts) ? accounts : [];
        } catch {
            return [];
        }
    };

    saveSavedBankAccounts = (accounts: SavedBankAccount[], webOrderToken: string) => {
        const key = this.savedBankAccountsStorageKey(webOrderToken);
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(accounts));
        } catch {
            /* ignore quota / private mode */
        }
    };

    componentDidMount() {
        if (typeof document !== 'undefined') {
            if (!document.getElementById(GLOBAL_STYLE_ID)) {
                const styleElement = document.createElement("style");
                styleElement.id = GLOBAL_STYLE_ID;
                styleElement.innerHTML = globalStyles;
                document.head.appendChild(styleElement);
            }
        }
        const tokenFromUrl = this.props.location?.query?.token;

        if (!tokenFromUrl) {
            this.setState({
                errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message,
                isLoading: false,
            });
            return;
        }

        try {
            localStorage.removeItem(LEGACY_ESTIMATE_APPROVE_SAVED_CARDS_KEY);
            localStorage.removeItem(LEGACY_ESTIMATE_APPROVE_SAVED_BANK_KEY);
        } catch {
            /* ignore */
        }

        this.fetchOrderDetails(tokenFromUrl);
    }

    formatOrderAmount = (order: WebOrderFromToken): string => {
        if (typeof order.totalOrderAmount === "number" && Number.isFinite(order.totalOrderAmount)) {
            return order.totalOrderAmount.toFixed(2);
        }
        return "";
    };

    paymentDescriptionForOrder = (order: WebOrderFromToken): string => {
        if (order.documentNumber != null && Number.isFinite(order.documentNumber)) {
            return `Deposit for estimate (doc ${order.documentNumber})`;
        }
        return "Deposit for estimate";
    };

    fetchOrderDetails = (token: string) => {
        fetchWebOrderFromToken(token, 
            (order) => {
                const amount = this.formatOrderAmount(order);
                const qbId = order.qbCustomerId != null ? String(order.qbCustomerId).trim() : "";
                const paymentTerms =
                    typeof order.paymentTerms === "string" ? order.paymentTerms.trim() || null : null;
                const requiresPrepaidCheckout = paymentTerms === PREPAID_CREDIT_CARD_PAYMENT_TERMS;
                const shippingAddress = Object.prototype.hasOwnProperty.call(order, "shippingAddress")
                    ? (order.shippingAddress || "").trim()
                    : undefined;
                this.setState(
                    {
                        webOrderNumber: order.webOrderNumber,
                        amount: amount || "0.00",
                        description: this.paymentDescriptionForOrder(order),
                        customerId: qbId || this.state.customerId,
                        paymentTerms,
                        shippingAddress,
                        shippingAddressConfirmed: false,
                        estimatePdfAvailable: order.estimatePdfAvailable === true && this.getWebOrderTokenValue().trim().length > 0,
                        isLoading: false,
                        errorMessage: null,
                    },
                    () => {
                        if (requiresPrepaidCheckout) {
                            this.refreshSavedCardsFromBackend();
                        }
                    }
                );
            }, 
            (error) => {
                this.setState({ 
                    errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message, 
                    isLoading: false,
                    paymentTerms: null,
                    estimatePdfAvailable: false,
                });
                console.error("Error fetching webOrderNumber:", error);
            }
        );
    };
    
    addressConfirmationApplies = (): boolean => typeof this.state.shippingAddress === "string";

    addressIsReadyToApprove = (): boolean => {
        if (!this.addressConfirmationApplies()) return true;
        const text = (this.state.shippingAddress || "").trim();
        return text.length > 0 && this.state.shippingAddressConfirmed;
    };

    handleApprove = () => {
        const token = this.props.location?.query?.token;
        const { webOrderNumber } = this.state;

        if (!this.addressIsReadyToApprove()) {
            return;
        }
    
        if (!token || !webOrderNumber) {
            this.setState({ 
                errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message, 
            });
            return;
        }
    
        this.setState({ isSubmitting: true });
    
        updateEstimateWithToken(
            token,
            "Approved",
            null,
            (response) => {
                this.setState({ isSubmitting: false, showConfirmation: true });
            },
            (error) => {
                this.setState({ 
                    errorMessage: typeof error === "string" && error
                        ? error
                        : "We encountered an issue submitting your request. Please try again in a moment.",
                    isSubmitting: false,
                });
                console.error("Error updating estimate status:", error);
            }
        );
    };

    invokeEstimateApprovalAfterPayment = () => {
        const token = this.props.location?.query?.token;
        const { webOrderNumber } = this.state;
        if (!token || !webOrderNumber) {
            this.setState({
                isPaymentProcessing: false,
                processingStep: null,
                errorMessage: ErrorMessages.ESTIMATE_ALREADY_PROCESSED.message,
            });
            return;
        }
        updateEstimateWithToken(
            token,
            "Approved",
            null,
            () => {
                this.setState({
                    isPaymentProcessing: false,
                    processingStep: null,
                    showConfirmation: true,
                });
            },
            (error) => {
                this.setState({
                    isPaymentProcessing: false,
                    processingStep: null,
                    errorMessage:
                        typeof error === "string" && error
                            ? error
                            : "Your payment was processed, but we could not approve the estimate. Please contact support with your order number.",
                });
                console.error("Error updating estimate status after payment:", error);
            }
        );
    };

    setPaymentMode = (mode: "new" | "saved") => {
        this.setState({ paymentMode: mode, paymentError: null }, () => {
            if (mode === "saved") {
                this.clearBinFundingPreview();
            } else {
                this.updateBinPreviewFromNumber(this.state.cardInput.number);
            }
        });
    };

    setPaymentRail = (rail: "card" | "ach") => {
        this.setState({ paymentRail: rail, paymentError: null, processingStep: null }, () => {
            if (rail === "ach") {
                this.clearBinFundingPreview();
            } else if (this.state.paymentMode === "new") {
                this.updateBinPreviewFromNumber(this.state.cardInput.number);
            }
        });
    };

    setAchMode = (mode: "new" | "saved") => {
        this.setState({ achMode: mode, paymentError: null });
    };

    onCardInputChange = (field: keyof CardInput, value: string | number) => {
        this.setState(
            (prev) => ({
                cardInput: {
                    ...prev.cardInput,
                    [field]: value,
                },
            }),
            () => {
                if (field === "number") {
                    this.updateBinPreviewFromNumber(this.state.cardInput.number);
                }
            }
        );
    };

    onAchInputChange = (field: "accountHolderName" | "routingNumber" | "accountNumber" | "accountType", value: string) => {
        this.setState((prev) => ({
            achInput: {
                ...prev.achInput,
                [field]: value,
            },
        }));
    };

    getParsedAmount = (): number => Number(this.state.amount) || 0;

    getProcessingFee = (): number => {
        const amount = this.getParsedAmount();
        if (!amount || amount <= 0) return 0;
        if (this.state.paymentRail === "ach") {
            return 0;
        }
        if (this.getEffectiveFundingForFee() === "Debit") {
            return 0;
        }
        return amount * CARD_PROCESSING_FEE_RATE;
    };

    getTotalDue = (): number => this.getParsedAmount() + this.getProcessingFee();

    getCardTotalDueForFunding = (funding: BackendCardFundingType | "unknown"): number => {
        const amount = this.getParsedAmount();
        if (!amount || amount <= 0) return 0;
        if (funding === "Debit") return amount;
        return amount + amount * CARD_PROCESSING_FEE_RATE;
    };

    getPaymentErrorText = (error: ApiError | any): string => {
        const body = error?.body;
        const parts: string[] = [];
        const pushUnique = (value: unknown) => {
            if (typeof value !== "string") return;
            const trimmed = value.trim();
            if (!trimmed) return;
            const normalized = trimmed.replace(/\.+$/, "").toLowerCase();
            if (parts.some((existing) => existing.replace(/\.+$/, "").toLowerCase() === normalized)) {
                return;
            }
            parts.push(trimmed);
        };
        pushUnique(error?.message);
        if (typeof body === "string") pushUnique(body);
        pushUnique(body?.message);
        if (Array.isArray(body?.errors)) {
            body.errors.forEach((item: any) => {
                if (typeof item === "string") pushUnique(item);
                pushUnique(item?.message);
            });
        }
        return this.toCustomerFacingPaymentError(parts.join(" ") || "Payment request failed.");
    };

    toCustomerFacingPaymentError = (message: string): string => {
        const m = String(message || "").trim();
        if (/intuit payments card save conflicted|card may already exist/i.test(m)) {
            return "This card may already be on file. Please select it from your saved cards, or try a different card.";
        }
        if (/intuit payments card save failed|couldn't save your card/i.test(m)) {
            return "Payment could not be completed. Please check your card details and try again.";
        }
        if (/intuit payments bank save conflicted|bank may already exist/i.test(m)) {
            return "This bank account may already be on file. Please select it from your saved accounts, or try a different account.";
        }
        if (/intuit payments bank save failed|couldn't save your bank account/i.test(m)) {
            return "Payment could not be completed. Please check your account details and try again.";
        }
        if (/intuit payments charge failed/i.test(m)) {
            return "Payment failed. Please update your card information and try again.";
        }
        if (/intuit payments ach debit failed/i.test(m)) {
            return "Your bank payment failed. Please update your payment information and try again.";
        }
        if (/unexpected error while (saving card on file|saving bank account on file|creating intuit payments charge|charging saved card)/i.test(m)) {
            return "Payment failed. Please update your payment information and try again.";
        }
        return m || "Payment request failed.";
    };

    getAchPaymentErrorText = (error: ApiError | any): string => {
        const body = error?.body;
        const hasProviderDetail =
            (body && typeof body === "object" && ("providerError" in body || "providerBody" in body)) ||
            false;
        if (hasProviderDetail) {
            console.error("[EstimateApprove][ach] provider error", {
                status: error?.status,
                body,
            });
            return "Your bank payment failed. Please update your payment information and try again.";
        }
        return this.getPaymentErrorText(error);
    };

    buildAchDevicePayload = (): { id?: string; type: string } => {
        let sessionId = "";
        try {
            sessionId = window.sessionStorage?.getItem("tgcc.payments.sessionId") || "";
            if (!sessionId && window.crypto && typeof window.crypto.getRandomValues === "function") {
                const bytes = new Uint8Array(8);
                window.crypto.getRandomValues(bytes);
                sessionId = "tgcc-" + Array.prototype.map.call(bytes, (b: number) => ("0" + b.toString(16)).slice(-2)).join("");
                window.sessionStorage?.setItem("tgcc.payments.sessionId", sessionId);
            }
        } catch {
            // sessionStorage / crypto can be blocked; the field is optional.
        }
        return sessionId ? { id: sessionId, type: "Web" } : { type: "Web" };
    };

    validatePayment = (): string | null => {
        const { paymentMode, paymentRail, achMode, selectedSavedCardId, selectedSavedBankAccountId, amount, customerId, cardInput, achInput } = this.state;
        const parsedAmount = Number(amount);
        if (!customerId) return "Customer ID is required.";
        if (!parsedAmount || parsedAmount <= 0) return "Amount must be greater than 0.";
        if (paymentRail === "card") {
            if (paymentMode === "saved" && !selectedSavedCardId) return "Select a saved card.";
            if (paymentMode === "new") {
                if (!cardInput.number || !/^\d{12,19}$/.test(cardInput.number.replace(/\s+/g, ""))) return "Card number is invalid.";
                if (!cardInput.cvc || !/^\d{3,4}$/.test(cardInput.cvc)) return "CVC must be 3 or 4 digits.";
                if (!cardInput.name.trim()) return "Cardholder name is required.";
                if (!cardInput.postalCode.trim()) return "Postal code is required.";
                const mm = Number(cardInput.expMonth);
                const yyyy = Number(cardInput.expYear);
                const now = new Date();
                const nowYear = now.getFullYear();
                const nowMonth = now.getMonth() + 1;
                if (mm < 1 || mm > 12) return "Expiration month must be between 1 and 12.";
                if (yyyy < nowYear) return "Expiration year cannot be in the past.";
                if (yyyy === nowYear && mm < nowMonth) {
                    return "This card has expired. The expiration month is before the current month.";
                }
            }
        } else {
            if (achMode === "saved") {
                if (!selectedSavedBankAccountId) return "Select a saved bank account.";
            } else {
                if (!achInput.accountHolderName.trim()) return "Account holder name is required.";
                if (!/^\d{9}$/.test(achInput.routingNumber)) return "Routing number must be 9 digits.";
                if (!/^\d{4,17}$/.test(achInput.accountNumber)) return "Account number is invalid.";
            }
        }
        return null;
    };

    runCharge = async () => {
        const webOrderAuth = this.getWebOrderAuth();
        if (!webOrderAuth) {
            this.setState({
                paymentError: "Missing estimate token (UI-only mode). No backend calls were made.",
            });
            return;
        }

        const approveEstimateAfterPayment = this.state.paymentTerms === PREPAID_CREDIT_CARD_PAYMENT_TERMS;
        const hasAlreadyCharged = !!this.state.chargeResult && !!this.state.paymentId;
        if (approveEstimateAfterPayment && hasAlreadyCharged) {
            this.setState(
                {
                    isPaymentProcessing: true,
                    paymentError: null,
                    processingStep: "Retrying estimate approval...",
                },
                () => this.invokeEstimateApprovalAfterPayment()
            );
            return;
        }

        if (!this.addressIsReadyToApprove()) {
            const missingAddress = this.addressConfirmationApplies() && !(this.state.shippingAddress || "").trim();
            this.setState({
                paymentError: missingAddress
                    ? "This estimate has no shipping address. Request a change before you pay."
                    : "Confirm the shipping address before you pay.",
            });
            return;
        }

        const validationMessage = this.validatePayment();
        if (validationMessage) {
            this.setState({ paymentError: validationMessage });
            return;
        }

        const {
            paymentMode,
            paymentRail,
            achMode,
            cardInput,
            saveCardForFuture,
            saveBankForFuture,
            customerId,
            currency,
            description,
            selectedSavedCardId,
            savedCards,
            achInput,
            savedBankAccounts,
        } = this.state;

        if (paymentRail === "ach") {
            const totalChargeAmount = Number(this.getParsedAmount().toFixed(2));
            this.setState({ isPaymentProcessing: true, processingStep: "Preparing ACH payment...", paymentError: null, chargeResult: null, paymentId: null });
            try {
                let achCharge: ChargeResult;

                if (achMode === "saved") {
                    const bankIdToCharge = this.state.selectedSavedBankAccountId;
                    this.setState({ processingStep: "Charging bank account..." });
                    achCharge = await chargeSavedBank({
                        bankId: bankIdToCharge,
                        amount: totalChargeAmount,
                        currency,
                        description,
                        customerId,
                        device: this.buildAchDevicePayload(),
                    }, webOrderAuth);
                } else {
                    this.setState({ processingStep: "Tokenizing bank account..." });
                    const bankPayload = {
                        name: achInput.accountHolderName,
                        routingNumber: achInput.routingNumber,
                        accountNumber: achInput.accountNumber,
                        accountType: achInput.accountType === "savings" ? "PERSONAL_SAVINGS" : "PERSONAL_CHECKING",
                    };
                    const paymentsToken = await tokenizeBankInBrowser(bankPayload);
                    if (!paymentsToken) {
                        throw new Error("Bank tokenization completed but no token was returned.");
                    }

                    if (saveBankForFuture) {
                        this.setState({ processingStep: "Saving bank account..." });
                        const savedBank = await saveBankOnFile(
                            {
                                paymentsToken,
                                accountHolderName: achInput.accountHolderName,
                                customerId,
                                accountTypeHint: achInput.accountType,
                                last4Hint: achInput.accountNumber.slice(-4),
                            },
                            webOrderAuth
                        );

                        const updatedAccounts = [savedBank].concat(
                            savedBankAccounts.filter((a) => a.bankAccountId !== savedBank.bankAccountId)
                        );
                        this.saveSavedBankAccounts(updatedAccounts, this.getWebOrderTokenValue());
                        this.setState({
                            savedBankAccounts: updatedAccounts,
                            selectedSavedBankAccountId: savedBank.bankAccountId,
                        });

                        this.setState({ processingStep: "Charging saved bank account..." });
                        achCharge = await chargeSavedBank(
                            {
                                bankId: savedBank.bankAccountId,
                                amount: totalChargeAmount,
                                currency,
                                description,
                                customerId,
                                device: this.buildAchDevicePayload(),
                            },
                            webOrderAuth
                        );
                    } else {
                        this.setState({ processingStep: "Submitting bank payment..." });
                        achCharge = await chargeOneTimeBank(
                            {
                                paymentsToken,
                                amount: totalChargeAmount,
                                currency,
                                description,
                                customerId,
                                accountHolderName: achInput.accountHolderName,
                                device: this.buildAchDevicePayload(),
                            },
                            webOrderAuth
                        );
                    }
                }

                if (approveEstimateAfterPayment) {
                    const failedMsg = getFailedChargeUserMessage(achCharge);
                    if (failedMsg) {
                        this.setState({
                            isPaymentProcessing: false,
                            processingStep: null,
                            paymentError: failedMsg,
                            chargeResult: null,
                            paymentId: null,
                        });
                        return;
                    }
                    this.setState(
                        {
                            processingStep: "Approving estimate (bank payment pending settlement)...",
                            chargeResult: achCharge,
                            paymentId: achCharge.paymentId || null,
                            achInput: {
                                ...this.state.achInput,
                                routingNumber: "",
                                accountNumber: "",
                            },
                        },
                        () => this.invokeEstimateApprovalAfterPayment()
                    );
                } else {
                    const failedMsg = getFailedChargeUserMessage(achCharge);
                    if (failedMsg) {
                        this.setState({
                            isPaymentProcessing: false,
                            processingStep: null,
                            paymentError: failedMsg,
                            chargeResult: null,
                            paymentId: null,
                        });
                        return;
                    }
                    this.setState({
                        isPaymentProcessing: false,
                        processingStep: "Bank payment submitted, pending settlement",
                        chargeResult: achCharge,
                        paymentId: achCharge.paymentId || null,
                        achInput: {
                            ...this.state.achInput,
                            routingNumber: "",
                            accountNumber: "",
                        },
                    });
                    this.refreshSavedCardsFromBackend();
                }
            } catch (err) {
                const friendly = this.getAchPaymentErrorText(err);
                this.setState({
                    isPaymentProcessing: false,
                    processingStep: null,
                    paymentError: friendly,
                });
            }
            return;
        }

        this.setState({ isPaymentProcessing: true, processingStep: "Preparing payment...", paymentError: null, chargeResult: null, paymentId: null });

        try {
            let currentCardId = selectedSavedCardId;
            let charge: ChargeResult;

            if (paymentMode === "saved") {
                this.setState({ processingStep: "Charging..." });
                const selectedSaved = savedCards.find((c) => c.cardId === selectedSavedCardId);
                const savedFunding = selectedSaved?.cardFundingType
                    ? String(selectedSaved.cardFundingType).trim()
                    : "";
                const finalCardFunding = normalizeStoredCardFunding(savedFunding);
                const totalChargeAmount = Number(this.getCardTotalDueForFunding(finalCardFunding).toFixed(2));
                charge = await chargeSavedCard(
                    {
                        cardId: selectedSavedCardId,
                        amount: totalChargeAmount,
                        currency,
                        description,
                        customerId,
                        ...(savedFunding ? { cardFundingType: savedFunding } : {}),
                    },
                    webOrderAuth
                );
            } else {
                const cardPayload = {
                    ...cardInput,
                    number: cardInput.number.replace(/\s+/g, ""),
                };
                this.setState({ processingStep: "Looking up card type..." });
                const cardFundingType = await resolveCardFundingTypeFromCardNumber(cardPayload.number);
                const finalCardFunding = cardFundingType || "unknown";
                const totalChargeAmount = Number(this.getCardTotalDueForFunding(finalCardFunding).toFixed(2));
                this.setState({ processingStep: "Tokenizing card..." });
                const paymentsToken = await tokenizeCardInBrowser(cardPayload);
                const tokenPreview = this.getTokenPreview(paymentsToken);
                this.logCardSaveFlow("tokenization success", {
                    saveCardRequested: saveCardForFuture,
                    tokenPreview,
                    customerId,
                    webOrderNumber: webOrderAuth?.webOrderNumber,
                });
                if (!paymentsToken) {
                    throw new Error("Tokenization completed but no token was returned.");
                }

                if (saveCardForFuture) {
                    this.setState({ processingStep: "Saving card..." });
                    this.logCardSaveFlow("save-card requested", {
                        saveCardRequested: true,
                        tokenPreview,
                        customerId,
                        webOrderNumber: webOrderAuth?.webOrderNumber,
                    });

                    let savedCard: SavedCard;
                    try {
                        savedCard = await saveCardOnFile(
                            {
                                customerId,
                                cardholderName: cardInput.name,
                                paymentsToken,
                                ...(cardFundingType ? { cardFundingType } : {}),
                            },
                            webOrderAuth
                        );
                        this.logCardSaveFlow("backend save-card response", {
                            status: "success",
                            tokenPreview,
                            customerId,
                            savedCardId: savedCard.cardId,
                        });
                    } catch (saveErr) {
                        const apiErr = saveErr as ApiError;
                        this.logCardSaveFlow("backend save-card response", {
                            status: apiErr?.status || "error",
                            tokenPreview,
                            customerId,
                            providerMessage: apiErr?.body?.message || apiErr?.message,
                        });
                        throw saveErr;
                    }

                    currentCardId = savedCard.cardId;
                    const updated = [savedCard].concat(savedCards.filter((c) => c.cardId !== savedCard.cardId));
                    this.saveSavedCards(updated, this.getWebOrderTokenValue());
                    this.setState({ savedCards: updated, selectedSavedCardId: savedCard.cardId, cardId: savedCard.cardId });

                    this.setState({ processingStep: "Charging..." });
                    charge = await chargeSavedCard(
                        {
                            cardId: savedCard.cardId,
                            amount: totalChargeAmount,
                            currency,
                            description,
                            customerId,
                            ...(cardFundingType ? { cardFundingType } : {}),
                        },
                        webOrderAuth
                    );
                } else {
                    this.logCardSaveFlow("save-card requested", {
                        saveCardRequested: false,
                        tokenPreview,
                        customerId,
                        webOrderNumber: webOrderAuth?.webOrderNumber,
                    });

                    this.setState({ processingStep: "Charging..." });
                    charge = await chargeOneTimeToken(
                        {
                            token: paymentsToken,
                            amount: totalChargeAmount,
                            currency,
                            description,
                            customerId,
                            ...(cardFundingType ? { cardFundingType } : {}),
                        },
                        webOrderAuth
                    );
                }
            }

            if (approveEstimateAfterPayment) {
                const failedMsg = getFailedChargeUserMessage(charge);
                if (failedMsg) {
                    this.setState({
                        isPaymentProcessing: false,
                        processingStep: null,
                        paymentError: failedMsg,
                        chargeResult: null,
                        paymentId: null,
                    });
                    return;
                }
                this.setState(
                    {
                        processingStep: "Approving estimate...",
                        chargeResult: charge,
                        paymentId: charge.paymentId || null,
                        cardId: currentCardId || null,
                        cardInput: {
                            ...this.state.cardInput,
                            number: "",
                            cvc: "",
                        },
                    },
                    () => this.invokeEstimateApprovalAfterPayment()
                );
            } else {
                const failedMsg = getFailedChargeUserMessage(charge);
                if (failedMsg) {
                    this.setState({
                        isPaymentProcessing: false,
                        processingStep: null,
                        paymentError: failedMsg,
                        chargeResult: null,
                        paymentId: null,
                    });
                    return;
                }
                this.setState({
                    isPaymentProcessing: false,
                    processingStep: "Charge complete",
                    chargeResult: charge,
                    paymentId: charge.paymentId || null,
                    cardId: currentCardId || null,
                    cardInput: {
                        ...this.state.cardInput,
                        number: "",
                        cvc: "",
                    },
                });
            }
        } catch (err) {
            const errMessage = String((err as any)?.message || "");
            this.setState({
                isPaymentProcessing: false,
                processingStep: null,
                paymentError:
                    /tokenization provider is not available|Raw payment data is blocked/i.test(errMessage)
                        ? this.getRawDataBlockedMessage()
                        : this.getPaymentErrorText(err),
            });
        }
    };

    closeErrorPopup = () => {
        this.setState({ errorMessage: null });
    };

    getErrorHeading = (errorMessage: string | null): string => {
        if (!errorMessage) {
            return "Something went wrong";
        }

        const sharedHeading = getSharedEstimateErrorHeading(errorMessage);
        if (sharedHeading) {
            return sharedHeading;
        }

        return "Error";
    };

    handleRequestChanges = () => {
        const token = this.props.location?.query?.token || "";
        browserHistory.push(Tgcc.Routes.EditEstimate(token));
    };
    
    closeConfirmationPopup = () => {
        this.setState({ showConfirmation: false });
        browserHistory.push(Tgcc.Routes.DashBoard);
    };

    getConfirmationMessage = (): string => {
        const charge = this.state.chargeResult;
        const isAchPending = !!charge && (charge.paymentRail === "ach" || charge.isPending);
        if (isAchPending) {
            return (
                "Your estimate has been approved and your bank payment was submitted successfully. " +
                "ACH transfers typically settle in 1–3 business days; we'll email you when settlement completes. " +
                "Your order will now proceed to production."
            );
        }
        return "Your estimate has been approved successfully! Your order will now proceed to production. You'll receive updates on your order status via email.";
    };
    
    renderOrderSummary = () => {
        const totalNumber = Number(this.state.amount);
        const showTotal = Number.isFinite(totalNumber) && totalNumber > 0;
        const payNow = this.state.paymentTerms === PREPAID_CREDIT_CARD_PAYMENT_TERMS;
        if (!showTotal && !payNow) return null;
        return (
            <div style={styles.summaryStrip}>
                {showTotal ? (
                    <div style={styles.summaryStripItem}>
                        <div style={styles.metaLabel}>Estimate total</div>
                        <div style={styles.metaValue}>${totalNumber.toFixed(2)}</div>
                    </div>
                ) : null}
                {payNow ? (
                    <div style={styles.summaryStripItem}>
                        <div style={styles.metaLabel}>Payment due now</div>
                        <div style={styles.metaValueAccent}>{this.state.paymentTerms}</div>
                    </div>
                ) : null}
            </div>
        );
    };

    render() {
        const { webOrderNumber, isLoading, errorMessage, isSubmitting, showConfirmation } = this.state;
        const isCheckout = this.state.paymentTerms === PREPAID_CREDIT_CARD_PAYMENT_TERMS;

        return (
            <div style={styles.pageContainer}>
                {errorMessage && (
                    <EstimateErrorPopup 
                        message={errorMessage} 
                        heading={this.getErrorHeading(errorMessage)} 
                        onClose={this.closeErrorPopup} 
                    />
                )}
                {showConfirmation && (
                    <EstimateConfirmationPopup 
                        message={this.getConfirmationMessage()}
                        heading={"Estimate Approved"}
                        onClose={this.closeConfirmationPopup}
                    />
                )}
                
                <header style={styles.topBar} className="approve-topbar">
                    <img
                        src={Tgcc.ResolveUrl("~/images/2022Logo.png")}
                        alt="The Graphic Cow Company"
                        style={styles.logo}
                    />
                </header>

                <div
                    className="approve-page"
                    style={{
                        ...styles.contentContainer,
                        ...(isCheckout ? styles.contentContainerCheckout : {}),
                    }}
                >
                    {isLoading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner}></div>
                            <p style={styles.loadingText}>Loading your estimate...</p>
                        </div>
                    ) : webOrderNumber ? (
                        <>
                            <section className="approve-layout" style={styles.layout}>
                                <div style={styles.heroColumn}>
                                    <p style={styles.eyebrow}>Estimate review</p>
                                    {webOrderNumber ? (
                                        <div className="approve-order-heading" style={styles.orderHeading}>
                                            <p style={styles.orderLine}>Order {webOrderNumber}</p>
                                            {this.state.estimatePdfAvailable ? (
                                                <a
                                                    className="view-estimate-link"
                                                    href={estimatePdfFromTokenUrl(this.getWebOrderTokenValue())}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={styles.viewEstimate}
                                                >
                                                    View estimate
                                                </a>
                                            ) : null}
                                        </div>
                                    ) : null}
                                    <h1 style={styles.title}>Review before you approve.</h1>
                                    <p style={styles.subtitle}>
                                        Please confirm the shipping address below. Approving this estimate sends your order to production.
                                    </p>
                                    {this.renderOrderSummary()}
                                    <ShippingAddressConfirm address={this.state.shippingAddress} />
                                    <button
                                        type="button"
                                        className="requestButton"
                                        style={styles.addressChange}
                                        onClick={this.handleRequestChanges}
                                    >
                                        <span aria-hidden>✎</span> Request an address change
                                    </button>
                                </div>

                                <aside className="approve-panel" style={styles.panel}>
                                    <div style={styles.panelHeader}>
                                        <div style={styles.panelIconCircle}>
                                            <span aria-hidden>✓</span>
                                        </div>
                                        <div>
                                            <h2 style={styles.panelTitle}>Final check</h2>
                                            <p style={styles.panelSubtitle}>
                                                {!(this.state.shippingAddress || "").trim() && this.addressConfirmationApplies()
                                                    ? "We need a shipping address to continue."
                                                    : "A quick sanity check before you approve."}
                                            </p>
                                        </div>
                                    </div>

                                    {!(this.state.shippingAddress || "").trim() && this.addressConfirmationApplies() ? (
                                        <p style={styles.shipLead}>
                                            There is no shipping address on this estimate. Request a change and include the full address.
                                        </p>
                                    ) : isLocalPickupAddress(this.state.shippingAddress || "") ? (
                                        <p style={styles.shipLead}>
                                            This order is a local pickup. Confirm this is the right place.
                                        </p>
                                    ) : (
                                        <>
                                            <ul style={styles.checkList}>
                                                <li>Suite or unit, if the building has one</li>
                                                <li>City and state</li>
                                                <li>ZIP code, including the last 4 digits</li>
                                            </ul>
                                            <p style={styles.shipNotice}>
                                                A missing suite or a wrong ZIP can add a carrier charge.
                                            </p>
                                        </>
                                    )}

                                    {(this.state.shippingAddress || "").trim() ? (
                                        <label style={styles.shipConfirmLabel}>
                                            <input
                                                type="checkbox"
                                                style={styles.shipCheckbox}
                                                checked={this.state.shippingAddressConfirmed}
                                                onChange={(event) => this.setState({ shippingAddressConfirmed: event.target.checked })}
                                            />
                                            <span style={styles.shipConfirmText}>
                                                {isLocalPickupAddress(this.state.shippingAddress || "")
                                                    ? "I will pick this up here"
                                                    : "This address is complete and correct"}
                                            </span>
                                        </label>
                                    ) : null}

                                    {!isCheckout ? (
                                        <div style={styles.buttonGroup}>
                                            <button
                                                type="button"
                                                className="approveButton"
                                                style={{
                                                    ...styles.approveButton,
                                                    ...(isSubmitting || !this.addressIsReadyToApprove() ? styles.approveButtonDisabled : {}),
                                                }}
                                                onClick={this.handleApprove}
                                                disabled={isSubmitting || !this.addressIsReadyToApprove()}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span style={styles.buttonSpinner}></span>
                                                        Approving...
                                                    </>
                                                ) : (
                                                    <>Approve estimate</>
                                                )}
                                            </button>
                                            <p className="approve-next" style={styles.nextStep}>
                                                After you approve, your order goes to production and you will receive status updates by email.
                                            </p>
                                            <button
                                                type="button"
                                                className="requestChangeButton"
                                                style={styles.requestChangeButton}
                                                onClick={this.handleRequestChanges}
                                            >
                                                Request a change
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={styles.buttonGroup}>
                                            <p style={styles.payNextCue}>Confirm the address, then pay below.</p>
                                            <button
                                                type="button"
                                                className="requestChangeButton requestChangeButtonSecondary"
                                                style={{ ...styles.requestChangeButton, ...styles.requestChangeSecondary }}
                                                onClick={this.handleRequestChanges}
                                            >
                                                Request a change
                                            </button>
                                        </div>
                                    )}
                                </aside>
                            </section>

                            {isCheckout ? (
                            <div className="estimate-payment-shell" style={styles.paymentShell}>
                                <div style={styles.paymentSectionHead}>
                                    <p style={styles.eyebrow}>Payment</p>
                                    <h2 style={styles.paymentSectionTitle}>Payment details</h2>
                                    <p style={styles.paymentSectionLead}>
                                        Debit cards have no processing fee. Credit and prepaid use a flat 3%. Bank transfer (ACH) is always fee-free.
                                    </p>
                                </div>
                                <div className="estimate-payment-layout" style={styles.paymentLayout}>
                                    <div style={styles.paymentLeft}>
                                        <div style={styles.paymentFormCard}>
                                            <FeeComparisonPanel paymentRail={this.state.paymentRail} onSelectRail={this.setPaymentRail} />
                                            {this.state.paymentRail === "card" ? (
                                                <PaymentMethodSelector
                                                    paymentMode={this.state.paymentMode}
                                                    onSelectMode={this.setPaymentMode}
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "card" && this.state.paymentMode === "new" ? (
                                                <CardForm
                                                    cardInput={this.state.cardInput}
                                                    saveCardForFuture={this.state.saveCardForFuture}
                                                    onInputChange={this.onCardInputChange}
                                                    onToggleSave={(checked) => this.setState({ saveCardForFuture: checked })}
                                                    showFundingFeeHint
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "card" && this.state.paymentMode === "saved" ? (
                                                <SavedCardList
                                                    cards={this.state.savedCards}
                                                    selectedCardId={this.state.selectedSavedCardId}
                                                    onSelect={(cardId) => this.setState({ selectedSavedCardId: cardId })}
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "ach" ? (
                                                <>
                                                    <AchModeSelector
                                                        achMode={this.state.achMode}
                                                        onSelectMode={this.setAchMode}
                                                    />
                                                    {this.state.achMode === "new" ? (
                                                        <AchForm
                                                            achInput={this.state.achInput}
                                                            saveBankForFuture={this.state.saveBankForFuture}
                                                            onToggleSaveBank={(checked) => this.setState({ saveBankForFuture: checked })}
                                                            onInputChange={this.onAchInputChange}
                                                        />
                                                    ) : (
                                                        <SavedBankAccountList
                                                            accounts={this.state.savedBankAccounts}
                                                            selectedBankAccountId={this.state.selectedSavedBankAccountId}
                                                            onSelect={(bankAccountId) => this.setState({ selectedSavedBankAccountId: bankAccountId })}
                                                        />
                                                    )}
                                                </>
                                            ) : null}
                                            {this.state.paymentRail === "card" ? (
                                                <CardFundingInsight
                                                    styles={styles}
                                                    paymentMode={this.state.paymentMode}
                                                    binPreviewPhase={this.state.binPreviewPhase}
                                                    effectiveFunding={this.getEffectiveFundingForFee()}
                                                    subtotal={this.getParsedAmount()}
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "ach" ? (
                                                <AchAuthorizationNotice amount={this.getTotalDue()} />
                                            ) : null}
                                            {this.state.paymentError ? (
                                                <div
                                                    ref={this.paymentErrorBannerRef}
                                                    role="alert"
                                                    aria-live="assertive"
                                                    style={styles.paymentErrorBanner}
                                                >
                                                    <div style={styles.paymentErrorIcon} aria-hidden>!</div>
                                                    <div>
                                                        <div style={styles.paymentErrorBannerTitle}>Payment unsuccessful</div>
                                                        <div style={styles.paymentErrorBannerBody}>{this.state.paymentError}</div>
                                                    </div>
                                                </div>
                                            ) : null}
                                            {!(this.state.chargeResult && this.state.paymentId) && !this.addressIsReadyToApprove() ? (
                                                <div style={styles.helperCallout}>
                                                    <span aria-hidden style={styles.helperCalloutIcon}>ℹ</span>
                                                    {(this.state.shippingAddress || "").trim()
                                                        ? "Confirm the shipping address above before you pay."
                                                        : "Request a change so a shipping address can be added before you pay."}
                                                </div>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="estimate-pay-button"
                                                style={{
                                                    ...styles.paymentSubmitButton,
                                                    ...(this.state.isPaymentProcessing ||
                                                    (!(this.state.chargeResult && this.state.paymentId) && !this.addressIsReadyToApprove())
                                                        ? styles.buttonDisabled
                                                        : {}),
                                                }}
                                                onClick={this.runCharge}
                                                disabled={
                                                    this.state.isPaymentProcessing ||
                                                    (!(this.state.chargeResult && this.state.paymentId) && !this.addressIsReadyToApprove())
                                                }
                                            >
                                                {this.state.isPaymentProcessing ? (
                                                    <>
                                                        <span style={styles.buttonSpinner}></span>
                                                        Processing...
                                                    </>
                                                ) : this.state.paymentTerms === PREPAID_CREDIT_CARD_PAYMENT_TERMS &&
                                                  this.state.chargeResult &&
                                                  this.state.paymentId
                                                    ? "Retry Estimate Approval"
                                                    : `Pay $${this.getTotalDue().toFixed(2)} and Approve Estimate`}
                                            </button>
                                            {this.state.processingStep && (
                                                <div style={styles.helperText}>
                                                    <span style={styles.processingDot} /> Status: {this.state.processingStep}
                                                </div>
                                            )}
                                            <ChargeResultPanel result={this.state.chargeResult} />
                                        </div>
                                    </div>
                                    <div style={styles.paymentRight}>
                                        <div className="estimate-payment-summary" style={styles.summaryCard}>
                                            <h4 style={styles.summaryTitle}>Order summary</h4>
                                            <div style={styles.summarySubLabel}>Total you&apos;ll pay</div>
                                            <div
                                                className={this.getTotalDue() <= 0 ? "estimate-summary-total is-zero" : "estimate-summary-total"}
                                                style={styles.summaryTotal}
                                            >
                                                ${this.getTotalDue().toFixed(2)}
                                            </div>
                                            <p style={styles.summaryHint}>
                                                {this.state.paymentRail === "ach"
                                                    ? "Bank transfer — no processing fee."
                                                    : this.getProcessingFee() <= 0
                                                    ? "Debit card — no card processing fee."
                                                    : "Includes card processing (flat 3%)."}
                                            </p>
                                            <div style={styles.summaryDivider}></div>
                                            <div style={styles.summaryRow}>
                                                <span>Estimate subtotal</span>
                                                <span style={styles.summaryRowValue}>${this.getParsedAmount().toFixed(2)}</span>
                                            </div>
                                            <div style={styles.summaryRow}>
                                                <span>Card processing</span>
                                                <span style={styles.summaryRowValue}>
                                                    {this.state.paymentRail === "ach"
                                                        ? "$0.00"
                                                        : `$${this.getProcessingFee().toFixed(2)}`}
                                                </span>
                                            </div>
                                            {this.state.paymentRail === "card" ? (
                                                <div style={styles.summaryFeeDetail}>
                                                    {this.getEffectiveFundingForFee() === "Debit"
                                                        ? "No fee for debit cards."
                                                        : this.getEffectiveFundingForFee() === "unknown"
                                                        ? "Assumes credit/prepaid until we confirm your card at payment."
                                                        : "Applies to credit and prepaid cards (flat 3%)."}
                                                </div>
                                            ) : (
                                                <div style={styles.summaryFeeDetail}>ACH payments have no TGCC processing fee.</div>
                                            )}
                                            <div style={styles.summaryDivider}></div>
                                            <div style={styles.summaryRow}>
                                                <span>Order</span>
                                                <span style={styles.summaryRowValueMuted}>{webOrderNumber}</span>
                                            </div>
                                            <div style={styles.summaryRow}>
                                                <span>Currency</span>
                                                <span style={styles.summaryRowValueMuted}>{this.state.currency}</span>
                                            </div>
                                            <div style={styles.summaryRow}>
                                                <span>Paying with</span>
                                                <span style={styles.summaryRowValue}>
                                                    {this.state.paymentRail === "ach"
                                                        ? "eCheck (ACH)"
                                                        : this.state.paymentMode === "saved"
                                                        ? "Saved card"
                                                        : "New card"}
                                                </span>
                                            </div>
                                            {this.state.paymentRail === "card" ? (
                                                <div style={styles.summaryFundingStrip}>
                                                    <div style={styles.summaryFundingStripTitle}>Card type (for fees)</div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                                        <span style={fundingBadgeStyle(this.getEffectiveFundingForFee())}>
                                                            {fundingLabel(this.getEffectiveFundingForFee())}
                                                        </span>
                                                        <span style={styles.summaryFundingHint}>
                                                            {this.state.paymentMode === "new" && this.state.binPreviewPhase === "loading"
                                                                ? "Verifying…"
                                                                : this.state.paymentMode === "new" && this.state.binPreviewPhase === "short"
                                                                ? "Enter more digits to detect"
                                                                : null}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div style={styles.summaryDivider}></div>
                                            <div style={styles.summarySecurity}>
                                                <span aria-hidden style={styles.lockIcon}>🔒</span>
                                                Payments are encrypted and processed securely.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            ) : null}

                        </>
                    ) : null}
                </div>
            </div>
        );
    }
}

const styles: { [key: string]: React.CSSProperties } = {
    pageContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        background: "radial-gradient(1200px 600px at 20% -10%, #eef2ff 0%, rgba(238,242,255,0) 60%), radial-gradient(1000px 500px at 90% 0%, #ecfeff 0%, rgba(236,254,255,0) 55%), #f8fafc",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        padding: 0,
        color: "#0f172a",
    },
    topBar: {
        width: "100%",
        height: "76px",
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "saturate(180%) blur(12px)",
        WebkitBackdropFilter: "saturate(180%) blur(12px)",
        borderBottom: "1px solid rgba(226,232,240,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 40px",
        boxSizing: "border-box",
        position: "sticky",
        top: 0,
        zIndex: 20,
    },
    logo: {
        height: "44px",
        width: "auto",
        display: "block",
    },
    topOrderBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        background: "#f1f5f9",
        border: "1px solid #e2e8f0",
        borderRadius: "999px",
        padding: "0.4rem 0.85rem",
        fontSize: "0.82rem",
    },
    topOrderLabel: {
        color: "#64748b",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        fontSize: "0.68rem",
    },
    topOrderValue: {
        color: "#0f172a",
        fontWeight: 700,
    },
    contentContainer: {
        width: "100%",
        maxWidth: "1080px",
        marginTop: 0,
        padding: "120px 40px 80px",
        boxSizing: "border-box",
        animation: "slideUp 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
    },
    contentContainerCheckout: {
        maxWidth: "1080px",
    },
    layout: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 0.9fr) minmax(480px, 1fr)",
        gap: "40px",
        alignItems: "start",
    },
    heroColumn: {
        minWidth: 0,
    },
    eyebrow: {
        margin: "0 0 0.9rem 0",
        color: "#6366f1",
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
    },
    orderHeading: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        columnGap: "0.85rem",
        rowGap: "0.15rem",
        margin: "0 0 0.55rem 0",
        maxWidth: "100%",
        minWidth: 0,
    },
    orderLine: {
        margin: 0,
        color: "#0f172a",
        fontSize: "1.2rem",
        fontWeight: 700,
        letterSpacing: "-0.02em",
        minWidth: 0,
        maxWidth: "100%",
    },
    viewEstimate: {
        color: "#4f46e5",
        fontSize: "0.98rem",
        fontWeight: 700,
        lineHeight: 1.4,
        textDecoration: "underline",
        textUnderlineOffset: "0.18em",
        maxWidth: "100%",
    },
    title: {
        color: "#0b1220",
        fontSize: "2.6rem",
        fontWeight: 800,
        letterSpacing: "-0.035em",
        margin: "0 0 0.75rem 0",
        lineHeight: 1.08,
    },
    subtitle: {
        color: "#475569",
        fontSize: "1.1rem",
        lineHeight: 1.55,
        margin: "0 0 1.6rem 0",
        maxWidth: "38rem",
    },
    summaryStrip: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.75rem",
        margin: "0 0 1.5rem 0",
    },
    summaryStripItem: {
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "14px",
        padding: "0.75rem 1.05rem",
        minWidth: "160px",
        boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
    },
    metaLabel: {
        color: "#64748b",
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        marginBottom: "0.28rem",
    },
    metaValue: {
        color: "#0f172a",
        fontSize: "1.28rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
    },
    metaValueAccent: {
        color: "#4f46e5",
        fontSize: "1.05rem",
        fontWeight: 800,
    },
    panel: {
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "24px",
        padding: "1.75rem 1.6rem 1.5rem",
        boxShadow: "0 20px 40px -20px rgba(15,23,42,0.14), 0 1px 2px rgba(15,23,42,0.04)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        minWidth: 0,
        maxWidth: "100%",
        position: "sticky",
        top: "96px",
    },
    panelHeader: {
        display: "flex",
        alignItems: "flex-start",
        gap: "0.85rem",
        marginBottom: "1.15rem",
    },
    panelIconCircle: {
        width: "42px",
        height: "42px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
        color: "#4338ca",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: "1.15rem",
        flexShrink: 0,
        border: "1px solid #c7d2fe",
    },
    panelTitle: {
        margin: 0,
        color: "#0f172a",
        fontSize: "1.22rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
    },
    panelSubtitle: {
        margin: "0.2rem 0 0 0",
        color: "#64748b",
        fontSize: "0.86rem",
        lineHeight: 1.4,
    },
    checkList: {
        margin: "0 0 0.5rem 0",
        padding: "0 0 0 1.2rem",
        color: "#334155",
        fontSize: "1rem",
        lineHeight: 1.55,
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "6rem 2rem",
        textAlign: "center",
    },
    spinner: {
        width: "48px",
        height: "48px",
        border: "4px solid #e0e7ff",
        borderLeft: "4px solid #4f46e5",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "1rem",
    },
    loadingText: {
        color: "#64748b",
        fontSize: "1rem",
        margin: 0,
        fontWeight: 500,
    },
    buttonGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "0.85rem",
        marginTop: "0.85rem",
        paddingTop: 0,
        minWidth: 0,
        width: "100%",
        alignSelf: "stretch",
    },
    approveButton: {
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "white",
        padding: "1rem 1.25rem",
        fontSize: "1.05rem",
        borderRadius: "14px",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.55rem",
        boxShadow: "0 12px 24px -10px rgba(15,23,42,0.45)",
        fontWeight: 700,
        width: "100%",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
        letterSpacing: "0.005em",
    },
    buttonDisabled: {
        opacity: 0.6,
        cursor: "not-allowed",
    },
    approveButtonDisabled: {
        background: "#e2e8f0",
        color: "#94a3b8",
        cursor: "not-allowed",
        boxShadow: "none",
    },
    requestChangeButton: {
        background: "#ffffff",
        color: "#0f172a",
        padding: "0.95rem 1.25rem",
        fontSize: "1.05rem",
        borderRadius: "14px",
        border: "1.5px solid #64748b",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
        fontWeight: 700,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
        transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
        letterSpacing: "0.005em",
        whiteSpace: "normal",
        textAlign: "center",
        lineHeight: 1.3,
    },
    requestChangeSecondary: {
        background: "#ffffff",
        color: "#475569",
        padding: "0.7rem 1rem",
        fontSize: "0.95rem",
        fontWeight: 600,
        border: "1.5px solid #cbd5e1",
        boxShadow: "none",
    },
    payNextCue: {
        margin: 0,
        padding: "0.85rem 1rem",
        background: "#eef2ff",
        border: "1px solid #c7d2fe",
        borderRadius: "12px",
        color: "#312e81",
        fontSize: "0.95rem",
        fontWeight: 600,
        lineHeight: 1.45,
    },
    buttonSpinner: {
        width: "16px",
        height: "16px",
        border: "2px solid rgba(255,255,255,0.4)",
        borderLeft: "2px solid white",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        display: "inline-block",
    },
    helperText: {
        color: "#64748b",
        fontSize: "0.86rem",
        fontWeight: 500,
        margin: "0.5rem 0 0 0",
        textAlign: "left",
        lineHeight: 1.45,
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
    },
    processingDot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: "#4f46e5",
        boxShadow: "0 0 0 4px rgba(79,70,229,0.18)",
        animation: "pulse 1.4s ease-in-out infinite",
        display: "inline-block",
    },
    nextStep: {
        margin: 0,
        color: "#475569",
        fontSize: "1rem",
        lineHeight: 1.5,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },
    addressChange: {
        marginTop: "1rem",
        background: "transparent",
        border: "none",
        padding: "0.35rem 0",
        color: "#4f46e5",
        fontSize: "0.98rem",
        fontWeight: 700,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.4rem",
        justifyContent: "flex-start",
        width: "auto",
        textAlign: "left",
    },
    paymentErrorBanner: {
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "14px",
        padding: "0.9rem 1rem",
        margin: "0.9rem 0 0.9rem 0",
        color: "#7f1d1d",
        boxShadow: "0 8px 20px -10px rgba(127,29,29,0.25)",
        display: "flex",
        alignItems: "flex-start",
        gap: "0.75rem",
    },
    paymentErrorIcon: {
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        background: "#dc2626",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: "0.9rem",
        flexShrink: 0,
        marginTop: "1px",
    },
    paymentErrorBannerTitle: {
        fontSize: "0.98rem",
        fontWeight: 800,
        letterSpacing: "0.01em",
        marginBottom: "0.2rem",
        color: "#7f1d1d",
    },
    paymentErrorBannerBody: {
        fontSize: "0.9rem",
        fontWeight: 500,
        lineHeight: 1.5,
        color: "#991b1b",
    },
    helperCallout: {
        display: "flex",
        alignItems: "flex-start",
        gap: "0.6rem",
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
        borderRadius: "12px",
        padding: "0.75rem 0.9rem",
        margin: "0.9rem 0 0.5rem 0",
        color: "#1e40af",
        fontSize: "0.9rem",
        lineHeight: 1.45,
        fontWeight: 500,
    },
    helperCalloutIcon: {
        fontWeight: 800,
        color: "#1d4ed8",
    },
    paymentShell: {
        marginTop: "48px",
        paddingTop: "36px",
        borderTop: "1px solid #e2e8f0",
        background: "transparent",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
    },
    paymentSectionHead: {
        margin: "0 0 1.35rem 0",
        maxWidth: "100%",
    },
    paymentSectionTitle: {
        margin: "0 0 0.45rem 0",
        color: "#0b1220",
        fontSize: "1.85rem",
        fontWeight: 800,
        letterSpacing: "-0.03em",
        lineHeight: 1.15,
    },
    paymentSectionLead: {
        margin: 0,
        color: "#475569",
        fontSize: "1.05rem",
        lineHeight: 1.55,
        maxWidth: "100%",
    },
    paymentHero: {
        background: "linear-gradient(135deg, #0f172a 0%, #312e81 100%)",
        borderRadius: "24px 24px 0 0",
        padding: "1.6rem 1.75rem",
        color: "#ffffff",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "0.8rem",
    },
    paymentHeroTitle: {
        margin: 0,
        fontSize: "1.4rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
    },
    paymentHeroSubtitle: {
        margin: "0.4rem 0 0 0",
        color: "rgba(255,255,255,0.78)",
        fontSize: "0.95rem",
        lineHeight: 1.5,
        maxWidth: "48rem",
    },
    progressSteps: {
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        padding: "1rem 1.75rem",
        background: "#ffffff",
        borderLeft: "1px solid #e2e8f0",
        borderRight: "1px solid #e2e8f0",
        flexWrap: "wrap",
    },
    progressStep: {
        fontSize: "0.8rem",
        color: "#94a3b8",
        padding: "0.25rem 0",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    progressStepDone: {
        color: "#047857",
    },
    progressStepActive: {
        color: "#4338ca",
    },
    progressStepDot: {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        background: "#f1f5f9",
        color: "#94a3b8",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.7rem",
        fontWeight: 800,
        border: "1px solid #e2e8f0",
    },
    progressStepDotDone: {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        background: "#d1fae5",
        color: "#047857",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.72rem",
        fontWeight: 800,
        border: "1px solid #a7f3d0",
    },
    progressStepDotActive: {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        background: "#e0e7ff",
        color: "#4338ca",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.72rem",
        fontWeight: 800,
        border: "1px solid #c7d2fe",
    },
    progressConnector: {
        flex: "1 1 20px",
        height: "2px",
        minWidth: "20px",
        background: "linear-gradient(90deg, #c7d2fe, #e2e8f0)",
        borderRadius: "2px",
        maxWidth: "60px",
    },
    paymentLayout: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, 0.8fr)",
        gap: "40px",
        alignItems: "start",
        background: "transparent",
        padding: 0,
        border: "none",
        borderRadius: 0,
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
    },
    paymentLeft: {
        minWidth: 0,
        maxWidth: "100%",
    },
    paymentRight: {
        minWidth: 0,
        maxWidth: "100%",
    },
    paymentFormCard: {
        display: "flex",
        flexDirection: "column",
        gap: "0.9rem",
    },
    summaryCard: {
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "24px",
        padding: "1.5rem 1.4rem",
        boxShadow: "0 20px 40px -20px rgba(15,23,42,0.14), 0 1px 2px rgba(15,23,42,0.04)",
        position: "sticky",
        top: "96px",
        boxSizing: "border-box",
        minWidth: 0,
        maxWidth: "100%",
    },
    summaryTitle: {
        margin: 0,
        color: "#0f172a",
        fontSize: "1.08rem",
        fontWeight: 800,
        letterSpacing: "-0.01em",
    },
    summaryTotal: {
        marginTop: "0.4rem",
        color: "#4338ca",
        fontSize: "2.4rem",
        fontWeight: 800,
        letterSpacing: "-0.035em",
        lineHeight: 1,
    },
    summaryHint: {
        margin: "0.5rem 0 0 0",
        fontSize: "0.83rem",
        color: "#64748b",
        lineHeight: 1.45,
    },
    summaryDivider: {
        height: "1px",
        background: "linear-gradient(90deg, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent)",
        margin: "0.95rem 0",
    },
    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        flexWrap: "wrap",
        gap: "0.35rem 0.75rem",
        color: "#475569",
        fontSize: "0.88rem",
        marginBottom: "0.55rem",
        minWidth: 0,
    },
    summaryRowValue: {
        fontWeight: 700,
        color: "#0f172a",
        marginLeft: "auto",
        textAlign: "right",
        overflowWrap: "anywhere",
    },
    summaryRowValueMuted: {
        fontWeight: 600,
        color: "#334155",
        marginLeft: "auto",
        textAlign: "right",
        overflowWrap: "anywhere",
    },
    summarySecurity: {
        color: "#64748b",
        fontSize: "0.82rem",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
        gap: "0.45rem",
    },
    lockIcon: {
        fontSize: "0.9rem",
    },
    summarySubLabel: {
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.09em",
        marginTop: "0.85rem",
    },
    summaryFeeDetail: {
        fontSize: "0.78rem",
        color: "#64748b",
        lineHeight: 1.45,
        marginTop: "0.3rem",
    },
    summaryFundingStrip: {
        marginTop: "0.75rem",
        padding: "0.7rem 0.85rem",
        borderRadius: "12px",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
    },
    summaryFundingStripTitle: {
        fontSize: "0.7rem",
        fontWeight: 800,
        color: "#0f172a",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        marginBottom: "0.4rem",
    },
    summaryFundingHint: {
        fontSize: "0.76rem",
        color: "#64748b",
    },
    fundingCallout: {
        borderRadius: "16px",
        padding: "1rem 1.1rem",
        background: "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)",
        border: "1px solid #c7d2fe",
    },
    fundingCalloutTitle: {
        fontWeight: 800,
        fontSize: "0.85rem",
        color: "#3730a3",
        marginBottom: "0.35rem",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
    },
    fundingCalloutBody: {
        fontSize: "0.88rem",
        color: "#4338ca",
        lineHeight: 1.55,
    },
    paymentMethodTabs: {
        display: "flex",
        background: "#f1f5f9",
        borderRadius: "12px",
        padding: "4px",
        gap: "4px",
        border: "1px solid #e2e8f0",
    },
    paymentTab: {
        flex: 1,
        border: "none",
        padding: "0.7rem 0.9rem",
        borderRadius: "9px",
        fontWeight: 700,
        cursor: "pointer",
        background: "transparent",
        color: "#475569",
        transition: "all 0.18s ease",
        fontSize: "0.9rem",
        fontFamily: "inherit",
    },
    paymentTabActive: {
        background: "#ffffff",
        color: "#0f172a",
        boxShadow: "0 2px 8px -2px rgba(15,23,42,0.15), 0 1px 2px rgba(15,23,42,0.05)",
    },
    fieldGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
        gap: "1rem",
        width: "100%",
        minWidth: 0,
    },
    inputSectionCard: {
        border: "1px solid #e2e8f0",
        borderRadius: "20px",
        padding: "1.35rem 1.35rem 1.4rem",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
    },
    feeComparisonCard: {
        border: "1px solid #e2e8f0",
        borderRadius: "20px",
        padding: "1.2rem 1.25rem",
        background: "#ffffff",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
    },
    feeComparisonGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
        gap: "0.75rem",
        marginTop: "0.85rem",
        width: "100%",
        minWidth: 0,
    },
    feeOptionCard: {
        border: "1px solid #e2e8f0",
        borderRadius: "14px",
        background: "#ffffff",
        padding: "1rem 1.05rem",
        textAlign: "left",
        cursor: "pointer",
        transition: "all 0.18s ease",
        fontFamily: "inherit",
    },
    feeOptionCardActive: {
        border: "2px solid #4338ca",
        boxShadow: "0 12px 28px -16px rgba(67,56,202,0.45)",
        background: "#f5f3ff",
    },
    feeOptionTitle: {
        color: "#0f172a",
        fontWeight: 800,
        fontSize: "0.9rem",
        marginBottom: "0.4rem",
    },
    feeOptionValue: {
        color: "#4338ca",
        fontWeight: 800,
        fontSize: "0.95rem",
        marginBottom: "0.3rem",
    },
    feeOptionHint: {
        color: "#64748b",
        fontSize: "0.78rem",
        lineHeight: 1.4,
    },
    sectionTitle: {
        margin: 0,
        color: "#0f172a",
        fontSize: "1.05rem",
        fontWeight: 800,
        letterSpacing: "-0.01em",
    },
    inputWrap: {
        display: "block",
    },
    input: {
        width: "100%",
        maxWidth: "100%",
        padding: "0.85rem 0.95rem",
        borderRadius: "10px",
        border: "1px solid #d1d5db",
        background: "#ffffff",
        fontSize: "1rem",
        color: "#0f172a",
        boxSizing: "border-box",
        outline: "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        fontFamily: "inherit",
    },
    label: {
        display: "block",
        color: "#334155",
        fontSize: "0.85rem",
        marginBottom: "0.4rem",
        fontWeight: 600,
        letterSpacing: "0.005em",
    },
    checkboxRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        margin: "0.9rem 0 0 0",
        color: "#334155",
        fontSize: "0.9rem",
        fontWeight: 500,
        cursor: "pointer",
    },
    savedCardRow: {
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "0.85rem 0.95rem",
        marginBottom: "0.6rem",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        gap: "0.7rem",
        cursor: "pointer",
        transition: "all 0.15s ease",
    },
    savedCardRowActive: {
        border: "2px solid #4338ca",
        background: "#f5f3ff",
        boxShadow: "0 10px 24px -16px rgba(67,56,202,0.4)",
    },
    savedCardLabelWrap: {
        display: "flex",
        flexDirection: "column",
        gap: "0.18rem",
    },
    savedCardMeta: {
        color: "#64748b",
        fontSize: "0.78rem",
    },
    savedCardBadgeRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.35rem",
        marginTop: "0.5rem",
    },
    savedCardsContainer: {
        marginBottom: "0.5rem",
    },
    savedCardsHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        marginBottom: "0.85rem",
    },
    savedCardsHeaderIcon: {
        fontSize: "1.15rem",
    },
    savedCardsHeaderTitle: {
        margin: 0,
        fontSize: "0.9rem",
        fontWeight: 800,
        color: "#0f172a",
        letterSpacing: "-0.005em",
    },
    savedCardsCount: {
        marginLeft: "auto",
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#4338ca",
        background: "#eef2ff",
        padding: "0.25rem 0.65rem",
        borderRadius: "20px",
        border: "1px solid #c7d2fe",
    },
    savedCardsGrid: {
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
    },
    savedCardModern: {
        width: "100%",
        borderRadius: "18px",
        padding: "1.05rem 1.1rem",
        cursor: "pointer",
        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        textAlign: "left",
        fontFamily: "inherit",
    },
    savedCardModernSelected: {
        boxShadow: "0 18px 35px -18px rgba(15,23,42,0.5), 0 0 0 3px rgba(99,102,241,0.35)",
    },
    savedCardModernHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "0.85rem",
        paddingRight: "2.5rem",
    },
    savedCardModernBrand: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    savedCardModernLast4: {
        fontSize: "1rem",
        fontWeight: 700,
        letterSpacing: "0.12em",
        fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
    },
    savedCardModernDetails: {
        display: "flex",
        flexDirection: "column",
        gap: "0.3rem",
    },
    savedCardModernName: {
        fontSize: "0.88rem",
        fontWeight: 600,
        opacity: 0.92,
    },
    savedCardModernExpiry: {
        fontSize: "0.74rem",
        opacity: 0.75,
        fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
        letterSpacing: "0.05em",
    },
    savedCardModernCheck: {
        position: "absolute",
        top: "1rem",
        right: "1rem",
        width: "26px",
        height: "26px",
        background: "#10b981",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: "0.8rem",
        fontWeight: "bold",
        boxShadow: "0 6px 14px -4px rgba(16,185,129,0.5)",
    },
    emptyCardsState: {
        textAlign: "center",
        padding: "2rem 1rem",
        background: "#f8fafc",
        borderRadius: "16px",
        border: "2px dashed #e2e8f0",
    },
    emptyCardsIcon: {
        fontSize: "2.5rem",
        marginBottom: "0.75rem",
        opacity: 0.7,
    },
    emptyCardsTitle: {
        fontSize: "0.95rem",
        fontWeight: 700,
        color: "#334155",
        margin: "0 0 0.4rem 0",
    },
    emptyCardsText: {
        fontSize: "0.82rem",
        color: "#64748b",
        margin: 0,
    },
    cardPreview: {
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 55%, #4338ca 100%)",
        borderRadius: "16px",
        padding: "1.2rem 1.25rem",
        color: "#e0e7ff",
        marginBottom: "0.35rem",
        boxShadow: "0 16px 32px -22px rgba(15,23,42,0.55)",
        position: "relative",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
    },
    cardPreviewChip: {
        width: "40px",
        height: "30px",
        borderRadius: "6px",
        background: "linear-gradient(160deg, #fde68a, #f59e0b)",
        marginBottom: "1rem",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.15)",
    },
    cardPreviewNumber: {
        fontSize: "1.15rem",
        fontWeight: 600,
        letterSpacing: "0.08em",
        marginBottom: "0.9rem",
        fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        lineHeight: 1.4,
        maxWidth: "100%",
    },
    cardPreviewMetaRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: "0.35rem 0.75rem",
        fontSize: "0.8rem",
        letterSpacing: "0.06em",
        opacity: 0.85,
        fontWeight: 600,
        textTransform: "uppercase",
        maxWidth: "100%",
    },
    cardPreviewName: {
        minWidth: 0,
        flex: "1 1 8rem",
        overflowWrap: "anywhere",
    },
    cardPreviewExpiry: {
        flex: "0 0 auto",
    },
    paymentSubmitButton: {
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "white",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        padding: "1rem 1.25rem",
        fontSize: "1.05rem",
        borderRadius: "14px",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.55rem",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease",
        boxShadow: "0 12px 24px -10px rgba(15,23,42,0.45)",
        fontWeight: 700,
        letterSpacing: "0.005em",
        marginTop: "0.25rem",
        fontFamily: "inherit",
        whiteSpace: "normal",
        textAlign: "center",
        lineHeight: 1.3,
    },
    achAuthorizationCard: {
        marginTop: "0.4rem",
        padding: "0.8rem 0.95rem",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        color: "#475569",
        fontSize: "0.8rem",
        lineHeight: 1.5,
    },
    chargeResultCard: {
        marginTop: "0.9rem",
        background: "linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)",
        border: "1px solid #a7f3d0",
        borderRadius: "14px",
        padding: "1rem",
    },
    chargeResultTitle: {
        margin: "0 0 0.55rem 0",
        color: "#047857",
        fontSize: "0.95rem",
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    subtleHint: {
        color: "#64748b",
        fontSize: "0.86rem",
        margin: "0.4rem 0 0.85rem 0",
        lineHeight: 1.5,
    },
    shipLead: {
        margin: 0,
        color: "#475569",
        fontSize: "0.95rem",
        lineHeight: 1.5,
    },
    shipNotice: {
        margin: "0.9rem 0 0 0",
        padding: "0.85rem 1rem",
        background: "#f7f5f2",
        border: "1px solid #e7e2da",
        borderRadius: "12px",
        color: "#44403c",
        fontSize: "1rem",
        lineHeight: 1.5,
    },
    noticeIcon: {
        fontWeight: 800,
    },
    shipConfirmLabel: {
        marginTop: "1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.7rem",
        cursor: "pointer",
    },
    shipCheckbox: {
        width: "1.15rem",
        height: "1.15rem",
        accentColor: "#4338ca",
        flexShrink: 0,
        cursor: "pointer",
    },
    shipConfirmText: {
        color: "#0f172a",
        fontSize: "0.95rem",
        fontWeight: 600,
        lineHeight: 1.4,
    },
    feeOptionSub: {
        fontSize: "0.78rem",
        color: "#64748b",
        lineHeight: 1.45,
        marginTop: "0.25rem",
    },
    feeOptionHighlight: {
        fontSize: "0.9rem",
        color: "#047857",
        fontWeight: 800,
        marginTop: "0.1rem",
        marginBottom: "0.2rem",
    },
    cardHintMicro: {
        fontSize: "0.82rem",
        color: "#64748b",
        marginTop: "0.3rem",
        marginBottom: "0.3rem",
        lineHeight: 1.55,
    },
    labelCard: {
        border: "1px solid #e2e8f0",
        borderRadius: "20px",
        overflow: "hidden",
        background: "#ffffff",
        boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
    },
    labelHead: {
        padding: "0.85rem 1.5rem",
        borderBottom: "1px solid #f1f5f9",
        color: "#64748b",
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        background: "#f8fafc",
    },
    labelBody: {
        padding: "1.35rem 1.5rem 1.4rem",
    },
    whoLine: {
        margin: 0,
        color: "#0f172a",
        fontSize: "1.35rem",
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: "-0.015em",
    },
    streetLine: {
        margin: "0.65rem 0 0 0",
        color: "#1e293b",
        fontSize: "1.12rem",
        fontWeight: 500,
        lineHeight: 1.45,
    },
    placeLine: {
        margin: "0.15rem 0 0 0",
        color: "#1e293b",
        fontSize: "1.12rem",
        fontWeight: 600,
        lineHeight: 1.45,
    },
};

function shippingAddressLines(address: string): string[] {
    return address
        .replace(/\r\n/g, "\n")
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
}

function isLocalPickupAddress(address: string): boolean {
    return /local\s*pickup/i.test(address);
}

function presentShippingAddress(lines: string[]): { identity: string[]; street: string[]; place: string } {
    if (lines.length === 0) return { identity: [], street: [], place: "" };
    if (lines.length === 1) return { identity: [lines[0]], street: [], place: "" };
    const place = lines[lines.length - 1];
    const before = lines.slice(0, -1);
    const streetAt = before.findIndex((line) => /\d/.test(line));
    if (streetAt < 0) return { identity: before, street: [], place };
    return {
        identity: before.slice(0, streetAt),
        street: before.slice(streetAt),
        place,
    };
}

type ShippingAddressConfirmProps = {
    address?: string;
};

const ShippingAddressConfirm: React.FC<ShippingAddressConfirmProps> = ({ address }) => {
    if (typeof address !== "string") return null;

    const lines = shippingAddressLines(address);
    const localPickup = lines.length > 0 && isLocalPickupAddress(address);
    const presented = presentShippingAddress(lines);

    if (lines.length === 0) {
        return (
            <div style={styles.labelCard}>
                <div style={styles.labelHead}>
                    <span>Shipping</span>
                </div>
                <div style={styles.labelBody}>
                    <h2 style={styles.whoLine}>No shipping address yet</h2>
                    <p style={{ ...styles.shipLead, marginTop: "0.5rem" }}>
                        Request a change and include the full address, with any suite or unit.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.labelCard}>
            <div style={styles.labelHead}>
                <span>{localPickup ? "Pickup location" : "Shipping to"}</span>
            </div>
            <div style={styles.labelBody}>
                {presented.identity.map((line, index) => (
                    <p key={`who-${index}`} className="ship-address-line" style={{ ...styles.whoLine, marginTop: index === 0 ? 0 : "0.15rem" }}>
                        {line}
                    </p>
                ))}
                {presented.street.map((line, index) => (
                    <p key={`street-${index}`} className="ship-address-line" style={styles.streetLine}>
                        {line}
                    </p>
                ))}
                {presented.place ? (
                    <p className="ship-address-line" style={styles.placeLine}>{presented.place}</p>
                ) : null}
            </div>
        </div>
    );
};

const GLOBAL_STYLE_ID = 'estimate-approve-global-styles';
const globalStyles = `
    @keyframes slideUp {
        0% {
            opacity: 0;
            transform: translateY(20px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    @keyframes pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.3); opacity: 0.75; }
    }
    
    .approveButton:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 18px 32px -12px rgba(15,23,42,0.55);
    }

    .requestButton:hover:not(:disabled) {
        color: #4338ca;
        background: #eef2ff;
        border-color: #c7d2fe;
    }

    .requestChangeButton:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #334155;
        color: #0f172a;
    }

    .requestChangeButtonSecondary:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #94a3b8;
        color: #0f172a;
    }

    .estimate-pay-button:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 18px 32px -12px rgba(15,23,42,0.55);
    }

    .estimate-summary-total.is-zero {
        font-size: 1.55rem !important;
        color: #0f172a !important;
        letter-spacing: -0.02em !important;
        margin-top: 0.35rem !important;
    }

    .estimate-payment-shell,
    .estimate-payment-layout,
    .estimate-field-grid,
    .estimate-fee-grid,
    .card-preview,
    .estimate-pay-button {
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
    }

    .card-preview-number {
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .view-estimate-link:hover {
        color: #4338ca;
    }

    .approve-order-heading,
    .view-estimate-link,
    .requestChangeButton {
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
    }

    .view-estimate-link {
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .saved-card-item:hover {
        transform: translateY(-2px);
        box-shadow: 0 24px 40px -20px rgba(15, 23, 42, 0.25);
    }

    @media (max-width: 768px) {
        button {
            min-width: unset !important;
        }

        .estimate-payment-shell {
            margin-top: 2rem !important;
            padding-top: 1.5rem !important;
        }

        .estimate-payment-layout {
            display: block !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
        }

        .estimate-payment-summary {
            position: static !important;
            margin-top: 1.1rem;
            width: 100% !important;
        }

        .estimate-field-grid,
        .estimate-fee-grid {
            grid-template-columns: 1fr !important;
        }

        .card-preview-number {
            font-size: 0.95rem !important;
            letter-spacing: 0.04em !important;
        }

        .estimate-pay-button {
            width: 100% !important;
            max-width: 100% !important;
        }

        .requestChangeButtonSecondary {
            width: 100% !important;
        }

        .approve-page {
            padding: 28px 18px 64px !important;
        }

        .approve-layout {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            min-width: 0 !important;
            gap: 1.5rem !important;
        }

        .approve-panel {
            margin-top: 0;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            position: static !important;
            box-sizing: border-box !important;
        }

        .approve-next {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
        }

        .approve-order-heading {
            width: 100% !important;
        }

        .requestChangeButton {
            width: 100% !important;
            max-width: 100% !important;
        }

        .approve-topbar {
            padding: 0 18px !important;
        }

        .ship-address-line {
            font-size: 1.15rem !important;
        }
    }

    input:focus, select:focus, textarea:focus {
        border-color: #6366f1;
        box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
    }
`;

type PaymentMethodSelectorProps = {
    paymentMode: "new" | "saved";
    onSelectMode: (mode: "new" | "saved") => void;
};

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({ paymentMode, onSelectMode }) => (
    <div style={styles.paymentMethodTabs}>
        <button
            type="button"
            style={{ ...styles.paymentTab, ...(paymentMode === "new" ? styles.paymentTabActive : {}) }}
            onClick={() => onSelectMode("new")}
        >
            + New card
        </button>
        <button
            type="button"
            style={{ ...styles.paymentTab, ...(paymentMode === "saved" ? styles.paymentTabActive : {}) }}
            onClick={() => onSelectMode("saved")}
        >
            Saved card
        </button>
    </div>
);

type CardFormProps = {
    cardInput: CardInput;
    saveCardForFuture: boolean;
    onInputChange: (field: keyof CardInput, value: string | number) => void;
    onToggleSave: (checked: boolean) => void;
    showFundingFeeHint?: boolean;
};

const CardForm: React.FC<CardFormProps> = ({
    cardInput,
    saveCardForFuture,
    onInputChange,
    onToggleSave,
    showFundingFeeHint,
}) => (
    <div style={styles.inputSectionCard}>
        <div className="card-preview" style={styles.cardPreview}>
            <div style={styles.cardPreviewChip}></div>
            <div className="card-preview-number" style={styles.cardPreviewNumber}>{cardInput.number || "•••• •••• •••• ••••"}</div>
            <div className="card-preview-meta" style={styles.cardPreviewMetaRow}>
                <span style={styles.cardPreviewName}>{cardInput.name || "CARDHOLDER NAME"}</span>
                <span style={styles.cardPreviewExpiry}>
                    {cardInput.expMonth > 0 ? String(cardInput.expMonth).padStart(2, "0") : "MM"}/
                    {cardInput.expYear > 0 ? String(cardInput.expYear).slice(-2) : "YY"}
                </span>
            </div>
        </div>
        <p style={styles.subtleHint}>Enter your card details. Full card data is never shown after submit.</p>
        <div className="estimate-field-grid" style={styles.fieldGrid}>
            <InputField label="Card number" value={cardInput.number} placeholder="4111 1111 1111 1111" onChange={(value) => onInputChange("number", value.replace(/[^\d\s]/g, ""))} />
            <InputField label="Cardholder name" value={cardInput.name} placeholder="Full name on card" onChange={(value) => onInputChange("name", value)} />
            <ExpiryField
                expMonth={cardInput.expMonth}
                expYear={cardInput.expYear}
                onChange={(month, year) => {
                    onInputChange("expMonth", month);
                    onInputChange("expYear", year);
                }}
            />
            <InputField label="CVC" value={cardInput.cvc} placeholder="3-4 digits" onChange={(value) => onInputChange("cvc", value.replace(/[^\d]/g, ""))} />
            <InputField label="Postal code" value={cardInput.postalCode} placeholder="ZIP / postal code" onChange={(value) => onInputChange("postalCode", value)} />
        </div>
        {showFundingFeeHint ? (
            <p style={styles.cardHintMicro}>
                <strong>Fee tip:</strong> we read the first digits of your card to tell <strong>debit</strong> (no processing fee) from{" "}
                <strong>credit</strong> or <strong>prepaid</strong> (flat 3%). Your pay button always matches the total shown.
            </p>
        ) : null}
        <label style={styles.checkboxRow}>
            <input type="checkbox" checked={saveCardForFuture} onChange={(e) => onToggleSave(e.currentTarget.checked)} />
            Save this card for future orders
        </label>
    </div>
);

type AchFormProps = {
    achInput: {
        accountHolderName: string;
        routingNumber: string;
        accountNumber: string;
        accountType: "checking" | "savings";
    };
    saveBankForFuture: boolean;
    onToggleSaveBank: (checked: boolean) => void;
    onInputChange: (field: "accountHolderName" | "routingNumber" | "accountNumber" | "accountType", value: string) => void;
};

const AchForm: React.FC<AchFormProps> = ({ achInput, saveBankForFuture, onToggleSaveBank, onInputChange }) => (
    <div style={styles.inputSectionCard}>
        <p style={styles.sectionTitle}>eCheck (ACH)</p>
        <p style={styles.subtleHint}>
            No processing fee for eCheck. Bank payments are submitted right away but settle in 1–3 business days.
        </p>
        <div className="estimate-field-grid" style={styles.fieldGrid}>
            <InputField
                label="Account holder name"
                value={achInput.accountHolderName}
                placeholder="Name on bank account"
                onChange={(value) => onInputChange("accountHolderName", value)}
            />
            <InputField
                label="Routing number"
                value={achInput.routingNumber}
                placeholder="9 digits"
                onChange={(value) => onInputChange("routingNumber", value.replace(/[^\d]/g, ""))}
            />
            <InputField
                label="Account number"
                value={achInput.accountNumber}
                placeholder="Bank account number"
                onChange={(value) => onInputChange("accountNumber", value.replace(/[^\d]/g, ""))}
            />
            <label style={styles.inputWrap}>
                <span style={styles.label}>Account type</span>
                <select
                    style={styles.input}
                    value={achInput.accountType}
                    onChange={(e) => onInputChange("accountType", e.currentTarget.value)}
                >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                </select>
            </label>
        </div>
        <label style={styles.checkboxRow}>
            <input type="checkbox" checked={saveBankForFuture} onChange={(e) => onToggleSaveBank(e.currentTarget.checked)} />
            Save this bank account for future orders
        </label>
    </div>
);

type SavedCardListProps = {
    cards: SavedCard[];
    selectedCardId: string;
    onSelect: (cardId: string) => void;
};

const SavedCardList: React.FC<SavedCardListProps> = ({ cards, selectedCardId, onSelect }) => {
    const getCardBrandDetails = (brand: string) => {
        const brandUpper = (brand || "").toUpperCase();
        const brands: { [key: string]: { icon: any; background: string; iconColor: string; textColor: string } } = {
            VISA: { icon: (FaIcons as any).FaCcVisa, background: "linear-gradient(135deg, #1a1f71 0%, #0a0f4e 100%)", iconColor: "#1a1f71", textColor: "#ffffff" },
            MASTERCARD: { icon: (FaIcons as any).FaCcMastercard, background: "linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)", iconColor: "#eb001b", textColor: "#ffffff" },
            MAST: { icon: (FaIcons as any).FaCcMastercard, background: "linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)", iconColor: "#eb001b", textColor: "#ffffff" },
            AMEX: { icon: (FaIcons as any).FaCcAmex, background: "linear-gradient(135deg, #2e77d0 0%, #1a4f8b 100%)", iconColor: "#2e77d0", textColor: "#ffffff" },
            DISCOVER: { icon: (FaIcons as any).FaCcDiscover, background: "linear-gradient(135deg, #ff6000 0%, #e55c00 100%)", iconColor: "#ff6000", textColor: "#ffffff" },
            DISC: { icon: (FaIcons as any).FaCcDiscover, background: "linear-gradient(135deg, #ff6000 0%, #e55c00 100%)", iconColor: "#ff6000", textColor: "#ffffff" },
        };
        
        for (const [key, details] of Object.entries(brands)) {
            if (brandUpper.includes(key)) return details;
        }
        return { 
            icon: (FaIcons as any).FaCreditCard, 
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            iconColor: "#667eea",
            textColor: "#ffffff" 
        };
    };

    if (!cards.length) {
        return (
            <div style={styles.emptyCardsState}>
                <div style={styles.emptyCardsIcon}>💳</div>
                <p style={styles.emptyCardsTitle}>No saved cards yet</p>
                <p style={styles.emptyCardsText}>Save a card from "New card" mode to see it here</p>
            </div>
        );
    }

    return (
        <div style={styles.savedCardsContainer}>
            <div style={styles.savedCardsHeader}>
                <span style={styles.savedCardsHeaderIcon}>💳</span>
                <h3 style={styles.savedCardsHeaderTitle}>Your Saved Cards</h3>
                <span style={styles.savedCardsCount}>{cards.length} card{cards.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={styles.savedCardsGrid}>
                {cards.map((card) => {
                    const brandDetails = getCardBrandDetails(card.brand || "");
                    const BrandIcon = brandDetails.icon;
                    const isSelected = selectedCardId === card.cardId;
                    const expMonth = card.expMonth ? String(card.expMonth).padStart(2, "0") : "MM";
                    const expYear = card.expYear ? String(card.expYear).slice(-2) : "YY";
                    const last4 = card.last4 || "••••";
                    
                    return (
                        <button
                            key={card.cardId}
                            type="button"
                            onClick={() => onSelect(card.cardId)}
                            className="saved-card-item"
                            style={{
                                ...styles.savedCardModern,
                                background: isSelected ? brandDetails.background : "#ffffff",
                                border: isSelected ? "none" : "1px solid #e2e8f0",
                                transform: isSelected ? "scale(1.015)" : "scale(1)",
                                ...(isSelected ? styles.savedCardModernSelected : {}),
                            }}
                        >
                            <div style={styles.savedCardModernHeader}>
                                <div style={styles.savedCardModernBrand}>
                                    {BrandIcon && <BrandIcon size={30} style={{ color: isSelected ? "#ffffff" : brandDetails.iconColor }} />}
                                </div>
                                <div style={{ ...styles.savedCardModernLast4, color: isSelected ? "#ffffff" : "#0f172a" }}>
                                    •••• {last4}
                                </div>
                            </div>
                            <div style={styles.savedCardModernDetails}>
                                <div style={{ ...styles.savedCardModernName, color: isSelected ? "#ffffff" : "#334155" }}>
                                    {card.displayLabel || "Saved Card"}
                                </div>
                                <div style={{ ...styles.savedCardModernExpiry, color: isSelected ? "rgba(255,255,255,0.8)" : "#64748b" }}>
                                    Expires {expMonth}/{expYear}
                                </div>
                                <div style={styles.savedCardBadgeRow}>
                                    <span
                                        style={{
                                            ...fundingBadgeStyle(normalizeStoredCardFunding(card.cardFundingType)),
                                            ...(isSelected ? { background: "rgba(255,255,255,0.2)", color: "#fff", borderColor: "rgba(255,255,255,0.4)" } : {}),
                                        }}
                                    >
                                        {fundingLabel(normalizeStoredCardFunding(card.cardFundingType))}
                                    </span>
                                    {normalizeStoredCardFunding(card.cardFundingType) === "unknown" ? (
                                        <span
                                            style={{
                                                fontSize: "0.7rem",
                                                color: isSelected ? "rgba(255,255,255,0.85)" : "#64748b",
                                                fontWeight: 600,
                                                alignSelf: "center",
                                            }}
                                        >
                                            Standard fee until verified
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            {isSelected && (
                                <div style={styles.savedCardModernCheck}>
                                    ✓
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

type CardFundingInsightProps = {
    styles: { [key: string]: React.CSSProperties };
    paymentMode: "new" | "saved";
    binPreviewPhase: BinPreviewPhase;
    effectiveFunding: BackendCardFundingType | "unknown";
    subtotal: number;
};

const CardFundingInsight: React.FC<CardFundingInsightProps> = ({
    styles: st,
    paymentMode,
    binPreviewPhase,
    effectiveFunding,
    subtotal,
}) => {
    if (subtotal <= 0) return null;

    const stdFee = subtotal * CARD_PROCESSING_FEE_RATE;
    let title = "How your card total is calculated";
    let body: React.ReactNode;

    if (paymentMode === "saved") {
        title = "Saved card & fees";
        if (effectiveFunding === "Debit") {
            body = (
                <>
                    This card is on file as <strong>debit</strong>. There is <strong>no card processing fee</strong> — you pay only the estimate subtotal (
                    ${subtotal.toFixed(2)}).
                </>
            );
        } else if (effectiveFunding === "unknown") {
            body = (
                <>
                    We don&apos;t have a debit/credit type stored for this card. Your total assumes the <strong>standard card fee</strong> (flat 3% ≈ <strong>
                    ${stdFee.toFixed(2)}</strong>). After payment, the stored type updates for next time.
                </>
            );
        } else {
            body = (
                <>
                    On file as <strong>{effectiveFunding.toLowerCase()}</strong>. Standard processing applies: <strong>flat 3%</strong> (about <strong>
                    ${stdFee.toFixed(2)}</strong> on this order).
                </>
            );
        }
    } else {
        title = "New card & fees";
        if (binPreviewPhase === "short") {
            body = (
                <>
                    Enter at least <strong>8 digits</strong> of your card number. We&apos;ll look up the card range: <strong>debit</strong> = no fee,{" "}
                    <strong>credit / prepaid</strong> = flat 3%.
                </>
            );
        } else if (binPreviewPhase === "loading") {
            body = <>Looking up your card&apos;s fee category from the number you entered…</>;
        } else if (effectiveFunding === "Debit") {
            body = (
                <>
                    This range looks like <strong>debit</strong>. <strong>No card processing fee</strong> — total is the subtotal only (${subtotal.toFixed(2)}).
                </>
            );
        } else if (binPreviewPhase === "unknown") {
            body = (
                <>
                    We couldn&apos;t read the card type from the BIN. Total assumes <strong>credit/prepaid fees</strong> (~$
                    {stdFee.toFixed(2)}). If you use a debit card, the amount charged will match at payment time.
                </>
            );
        } else {
            body = (
                <>
                    Detected <strong>{effectiveFunding.toLowerCase()}</strong>. Processing fee ≈ <strong>${stdFee.toFixed(2)}</strong> (flat 3%).
                </>
            );
        }
    }

    return (
        <div style={st.fundingCallout}>
            <div style={st.fundingCalloutTitle}>
                <span aria-hidden>✨</span> {title}
            </div>
            <div style={st.fundingCalloutBody}>{body}</div>
        </div>
    );
};

type FeeComparisonPanelProps = {
    paymentRail: "card" | "ach";
    onSelectRail: (rail: "card" | "ach") => void;
};

const FeeComparisonPanel: React.FC<FeeComparisonPanelProps> = ({ paymentRail, onSelectRail }) => (
    <div style={styles.feeComparisonCard}>
        <p style={styles.sectionTitle}>Choose how you pay</p>
        <p style={styles.subtleHint}>Compare card vs bank — totals update automatically based on debit, credit, or ACH.</p>
        <div className="estimate-fee-grid" style={styles.feeComparisonGrid}>
            <button
                style={{ ...styles.feeOptionCard, ...(paymentRail === "card" ? styles.feeOptionCardActive : {}) }}
                onClick={() => onSelectRail("card")}
                type="button"
            >
                <div style={styles.feeOptionTitle}>Debit or credit card</div>
                <div style={styles.feeOptionHighlight}>Debit → no fee</div>
                <div style={styles.feeOptionSub}>Credit &amp; prepaid → flat 3% (shown before you pay).</div>
                <div style={styles.feeOptionHint}>Fast checkout; we detect card type from your number.</div>
            </button>
            <button
                style={{ ...styles.feeOptionCard, ...(paymentRail === "ach" ? styles.feeOptionCardActive : {}) }}
                onClick={() => onSelectRail("ach")}
                type="button"
            >
                <div style={styles.feeOptionTitle}>eCheck (ACH)</div>
                <div style={styles.feeOptionHighlight}>Always $0 fee</div>
                <div style={styles.feeOptionSub}>Pay directly from your bank — no processing fee from us.</div>
                <div style={styles.feeOptionHint}>Best when you want the lowest cost option.</div>
            </button>
        </div>
    </div>
);

type AchModeSelectorProps = {
    achMode: "new" | "saved";
    onSelectMode: (mode: "new" | "saved") => void;
};

const AchModeSelector: React.FC<AchModeSelectorProps> = ({ achMode, onSelectMode }) => (
    <div style={styles.paymentMethodTabs}>
        <button type="button" style={{ ...styles.paymentTab, ...(achMode === "new" ? styles.paymentTabActive : {}) }} onClick={() => onSelectMode("new")}>
            New bank account
        </button>
        <button type="button" style={{ ...styles.paymentTab, ...(achMode === "saved" ? styles.paymentTabActive : {}) }} onClick={() => onSelectMode("saved")}>
            Saved bank account
        </button>
    </div>
);

type SavedBankAccountListProps = {
    accounts: SavedBankAccount[];
    selectedBankAccountId: string;
    onSelect: (bankAccountId: string) => void;
};

const SavedBankAccountList: React.FC<SavedBankAccountListProps> = ({ accounts, selectedBankAccountId, onSelect }) => (
    <div style={styles.inputSectionCard}>
        {!accounts.length ? (
            <p style={styles.loadingText}>No saved bank accounts yet. Add one in "New bank account" mode.</p>
        ) : (
            accounts.map((account) => (
                <label
                    key={account.bankAccountId}
                    style={{
                        ...styles.savedCardRow,
                        ...(selectedBankAccountId === account.bankAccountId ? styles.savedCardRowActive : {}),
                    }}
                >
                    <input
                        type="radio"
                        name="saved-bank-account"
                        checked={selectedBankAccountId === account.bankAccountId}
                        onChange={() => onSelect(account.bankAccountId)}
                    />
                    <div style={styles.savedCardLabelWrap}>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{account.displayLabel}</span>
                        <span style={styles.savedCardMeta}>Bank account ID: {account.bankAccountId}</span>
                    </div>
                </label>
            ))
        )}
    </div>
);

type AchAuthorizationNoticeProps = {
    amount: number;
};

const AchAuthorizationNotice: React.FC<AchAuthorizationNoticeProps> = ({ amount }) => {
    const amountLabel = amount > 0 ? `$${amount.toFixed(2)}` : "the amount shown above";
    return (
        <div style={styles.achAuthorizationCard}>
            <strong>Authorization:</strong> By clicking <em>Pay {amountLabel}</em>, you authorize The Graphic Cow
            Company to electronically debit your bank account for {amountLabel}. ACH transfers typically settle in
            1–3 business days; you may revoke this authorization in writing before settlement by contacting us.
        </div>
    );
};

type ChargeResultPanelProps = {
    result: ChargeResult | null;
};

const ChargeResultPanel: React.FC<ChargeResultPanelProps> = ({ result }) => {
    if (!result) return null;
    const isAch = result.paymentRail === "ach" || !!result.isPending;
    const title = isAch ? "Bank payment submitted" : "Payment successful";
    return (
        <div style={styles.chargeResultCard}>
            <h4 style={styles.chargeResultTitle}>
                <span aria-hidden>✓</span> {title}
            </h4>
            {isAch ? (
                <div style={styles.featureItem}>
                    Pending settlement — ACH transfers typically settle in 1–3 business days.
                </div>
            ) : null}
            <div style={styles.featureItem}>Intuit charge id: {result.intuitChargeId || "-"}</div>
            <div style={styles.featureItem}>QBO payment id: {result.paymentId || "(pending)"}</div>
        </div>
    );
};

type InputFieldProps = {
    label: string;
    value: string;
    placeholder?: string;
    onChange: (value: string) => void;
};

const InputField: React.FC<InputFieldProps> = ({ label, value, placeholder, onChange }) => (
    <label style={styles.inputWrap}>
        <span style={styles.label}>{label}</span>
        <input style={styles.input} value={value} placeholder={placeholder} onChange={(e) => onChange(e.currentTarget.value)} />
    </label>
);

type ExpiryFieldProps = {
    expMonth: number;
    expYear: number;
    onChange: (month: number, year: number) => void;
};

const ExpiryField: React.FC<ExpiryFieldProps> = ({ expMonth, expYear, onChange }) => {
    const initialFromProps = (): string => {
        const m = expMonth > 0 ? String(expMonth).padStart(2, "0") : "";
        const y = expYear > 0 ? String(expYear).slice(-2) : "";
        if (m && y) return `${m}/${y}`;
        return m;
    };

    const [display, setDisplay] = React.useState<string>(initialFromProps);

    React.useEffect(() => {
        const next = initialFromProps();
        if (!next && expMonth === 0 && expYear === 0 && display !== "") {
            setDisplay("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expMonth, expYear]);

    const handle = (raw: string) => {
        const digits = raw.replace(/\D/g, "").slice(0, 4);
        let formatted = digits;
        if (digits.length >= 3) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else if (digits.length === 2 && raw.endsWith("/")) {
            formatted = `${digits}/`;
        }
        setDisplay(formatted);
        const monthNum = digits.length >= 2 ? Number(digits.slice(0, 2)) : 0;
        const yearNum = digits.length === 4 ? 2000 + Number(digits.slice(2)) : 0;
        onChange(monthNum, yearNum);
    };

    return (
        <label style={styles.inputWrap}>
            <span style={styles.label}>Expiration (MM/YY)</span>
            <input
                style={styles.input}
                value={display}
                placeholder="MM/YY"
                inputMode="numeric"
                autoComplete="cc-exp"
                maxLength={5}
                onChange={(e) => handle(e.currentTarget.value)}
            />
        </label>
    );
};

export { EstimateApprove };